import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DropDown from "../../Components/Shared/Dropdown/Dropdown";
import { fetchCourseData } from "./../../Redux/StudentRegistration/courseSlice";
import { FULL_API_BASE_URL } from "../../Config/Config";
import "./contentresources.css";

// ── Load YT IFrame API once globally ──────────────────────────────────────────
let ytApiLoaded = false;
let ytApiReady = false;
const ytReadyCallbacks = [];

function loadYTApi() {
  if (ytApiLoaded) return;
  ytApiLoaded = true;
  const tag = document.createElement("script");
  tag.src = "https://www.youtube.com/iframe_api";
  document.head.appendChild(tag);
  window.onYouTubeIframeAPIReady = () => {
    ytApiReady = true;
    ytReadyCallbacks.forEach((cb) => cb());
    ytReadyCallbacks.length = 0;
  };
}

function onYTReady(cb) {
  if (ytApiReady) { cb(); return; }
  ytReadyCallbacks.push(cb);
  loadYTApi();
}

function getYoutubeVideoId(url) {
  try {
    const u = new URL(url.trim());
    if (u.hostname === "youtu.be") return u.pathname.replace("/", "");
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1];
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/shorts/")[1];
    }
  } catch { /* ignore */ }
  return null;
}

function formatTime(sec) {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

function mapApiFolderToTree(node, parentPath = "") {
  const uniqueId = parentPath ? `${parentPath}-${node.folder_id}` : `f${node.folder_id}`;
  return {
    id: uniqueId,
    name: node.folder_name,
    is_real: node.is_real === true,
    isPendingNew: false,
    folderId: node.folder_id,
    studyContentId: node.study_content_id,
    docs: node.docs || [],
    videos: node.videos || [],
    children: (node.children || []).map((c) => mapApiFolderToTree(c, uniqueId)),
  };
}

// ── Get full path of a folder by id ──────────────────────────────────────
const getFolderPath = (folders, targetId, path = []) => {
  for (const f of folders) {
    const current = [...path, f.name];
    if (f.id === targetId) return current;
    if (f.children?.length) {
      const found = getFolderPath(f.children, targetId, current);
      if (found) return found;
    }
  }
  return null;
};

// ── Get the chain of ancestor folder ids above targetId (excludes targetId itself) ──
const findAncestorFolderIds = (folders, targetId, chain = []) => {
  for (const f of folders) {
    if (f.id === targetId) return chain;
    if (f.children?.length) {
      const found = findAncestorFolderIds(f.children, targetId, [...chain, f.id]);
      if (found) return found;
    }
  }
  return null;
};

// ── All content keys (pdf + yt) of a folder and every sub-folder ─────────
function subtreeContentKeys(node, pdfsMap, ytMap, out = []) {
  (pdfsMap[node.id] || []).forEach((_, i) => out.push(`${node.id}_pdf_${i}`));
  ((ytMap[node.id] || {}).list || []).forEach((_, i) => out.push(`${node.id}_yt_${i}`));
  (node.children || []).forEach((c) => subtreeContentKeys(c, pdfsMap, ytMap, out));
  return out;
}

// ── Folder ids that have AT LEAST ONE highlighted item in their subtree ──
// A folder stays checked while any of its content (or its sub-folders'
// content) is still highlighted. It only unchecks when nothing is left.
function computeCheckedFolderIds(nodes, contentSet, pdfsMap, ytMap, out = new Set()) {
  nodes.forEach((n) => {
    const keys = subtreeContentKeys(n, pdfsMap, ytMap);
    if (keys.some((k) => contentSet.has(k))) out.add(n.id);
    if (n.children?.length) computeCheckedFolderIds(n.children, contentSet, pdfsMap, ytMap, out);
  });
  return out;
}

function displayNameFromKey(key) {
  if (!key) return "file.pdf";
  const last = key.split("/").pop();
  const underscoreIdx = last.indexOf("_");
  return underscoreIdx >= 0 ? last.slice(underscoreIdx + 1) : last;
}

function pinnedFieldsFromPath(path) {
  const chapter_id = path[0] && path[0].is_real ? path[0].id : null;
  const topic_id = chapter_id !== null && path[1] && path[1].is_real ? path[1].id : null;
  return { chapter_id, topic_id };
}

function seedContentState(nodes, pdfsMap, ytMap, pdfBaseline, ytBaseline) {
  nodes.forEach((n) => {
    const pdfEntries = (n.docs || []).map((d) => ({
      fileName: displayNameFromKey(d.pdf_file_name),
      previewUrl: d.pdf_url,
      pdfFileName: d.pdf_file_name,
    }));
    pdfsMap[n.id] = pdfEntries;
    pdfBaseline[n.id] = pdfEntries.map((p) => p.pdfFileName);

    const ytLinks = (n.videos || []).map((v) => v.yt_video_url);
    ytMap[n.id] = { list: ytLinks, url: "", error: "" };
    ytBaseline[n.id] = ytLinks;

    if (n.children?.length) seedContentState(n.children, pdfsMap, ytMap, pdfBaseline, ytBaseline);
  });
}

function collectStudyContentIds(nodes, out) {
  nodes.forEach((n) => {
    if (n.studyContentId) out.add(n.studyContentId);
    if (n.children?.length) collectStudyContentIds(n.children, out);
  });
}

function collectDeletedRoots(originalNodes, currentIdSet, out) {
  originalNodes.forEach((n) => {
    if (n.studyContentId && !currentIdSet.has(n.studyContentId)) {
      out.push(n.studyContentId);
    } else if (n.children?.length) {
      collectDeletedRoots(n.children, currentIdSet, out);
    }
  });
}

function diffList(current, baseline) {
  const remaining = [...baseline];
  const added = [];
  current.forEach((item) => {
    const i = remaining.indexOf(item);
    if (i >= 0) remaining.splice(i, 1);
    else added.push(item);
  });
  return { added, removed: remaining };
}

function hasAnyContent(node, pdfsMap, ytMap) {
  const pdfs = pdfsMap[node.id] || [];
  const yts = (ytMap[node.id] || {}).list || [];
  if (pdfs.length > 0 || yts.length > 0) return true;
  return (node.children || []).some((c) => hasAnyContent(c, pdfsMap, ytMap));
}

async function saveStudyContentBatch({ uploadedBy, item, pdfFiles, accessToken }) {
  const formData = new FormData();
  formData.append("uploaded_by", uploadedBy);
  formData.append("items", JSON.stringify([item]));

  (pdfFiles || []).forEach((file) => {
    const renamed = new File([file], `0__${file.name}`, { type: file.type });
    formData.append("pdf_files", renamed);
  });

  const res = await fetch(`${FULL_API_BASE_URL}/save-study-content/`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: formData,
  });
  const json = await res.json();
  if (!res.ok || json?.status !== 200) {
    throw new Error(json?.message || `Request failed with status ${res.status}`);
  }
  return json.data[0];
}

function PdfViewer({ dataClient, setMessageBox, setIsMessageBoxVisible, setActiveImg }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userName = dataClient?.client_schema_name?.[6]?.[0]?.user_name;
  const { coursesdata } = useSelector((state) => state.course);

  const [isOpenSubject, setIsOpenSubject] = useState(true);

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  const [value, setValues] = useState({ course_id: "" });
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSubjectLabel, setSelectedSubjectLabel] = useState("");

  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // PDF modal
  const [modalPdfUrl, setModalPdfUrl] = useState(null);
  const [modalPdfName, setModalPdfName] = useState("");
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  // ── YouTube player modal state ─────────────────────────────────────────────
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const [ytVideoId, setYtVideoId] = useState(null);

  const ytDivRef = useRef(null);
  const playerRef = useRef(null);
  const timeUpdaterRef = useRef(null);
  const idleTimerRef = useRef(null);
  const containerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeFmt, setCurrentTimeFmt] = useState("0:00");
  const [totalTimeFmt, setTotalTimeFmt] = useState("0:00");
  const [progressVal, setProgressVal] = useState(0);
  const [durationVal, setDurationVal] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [uiHidden, setUiHidden] = useState(false);
  const [isKioskFS, setIsKioskFS] = useState(false);

  const [isOpenCustomFolder, setIsOpenCustomFolder] = useState(true);
  const [allFolders, setAllFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [folderPdfs, setFolderPdfs] = useState({});
  const [folderYoutube, setFolderYoutube] = useState({});
  const ytTitleCacheRef = useRef({});
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState("");

  const [isContentDirty, setIsContentDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const pendingBatchChange = useRef(null);

  const originalFoldersSnapshotRef = useRef([]);
  const folderPdfsBaselineRef = useRef({});
  const folderYoutubeBaselineRef = useRef({});

  // ── Import modal state ────────────────────────────────────────────────────
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSelectedBatch, setImportSelectedBatch] = useState(null);
  const [importTargetFolderId, setImportTargetFolderId] = useState(null);
  const [importCheckedIds, setImportCheckedIds] = useState(new Set());
  const [importCheckedContentIds, setImportCheckedContentIds] = useState(new Set());

  const [importFolders, setImportFolders] = useState([]);
  const [importFolderPdfs, setImportFolderPdfs] = useState({});
  const [importFolderYoutube, setImportFolderYoutube] = useState({});
  const [isImportFoldersLoading, setIsImportFoldersLoading] = useState(false);
  const [importFoldersError, setImportFoldersError] = useState("");
  const [importExpandedFolders, setImportExpandedFolders] = useState({});

  const [importContentWithFolder, setImportContentWithFolder] = useState(true);

  // ── Helper: show the YES/NO confirmation modal ────────────────────────────
  const showUnsavedWarning = (onConfirm) => {
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <div className="loader-tc"></div>
          <span>
            You have unsaved changes in the Content Folder. Changing batch
            details will discard them. Do you want to proceed?
          </span>
        </div>
        <div
          className="messagebox-insider2"
          style={{ display: "flex", gap: "10px", marginTop: "10px" }}
        >
          <button
            className="messagebox-cancel-btn-ok"
            onClick={() => {
              setIsMessageBoxVisible(false);
              onConfirm();
            }}
          >
            YES
          </button>
          <button
            className="messagebox-cancel-btn-ok"
            onClick={() => setIsMessageBoxVisible(false)}
          >
            NO
          </button>
        </div>
      </div>
    );
    setIsMessageBoxVisible(true);
  };

  const handleCourseChange = (sel) => {
    const doChange = () => {
      setValues({ course_id: sel });
      setIsContentDirty(false);
    };
    if (isContentDirty) showUnsavedWarning(doChange);
    else doChange();
  };

  const handleSubjectChange = (sel) => {
    const doChange = () => {
      setSelectedSubject(sel);
      setIsContentDirty(false);
    };
    if (isContentDirty) showUnsavedWarning(doChange);
    else doChange();
  };

  const handleBatchSelection = (id) => {
    const doChange = () => {
      setSelectedBatch(batches.find((b) => b.batch_id === id));
      setIsContentDirty(false);
    };
    if (isContentDirty) showUnsavedWarning(doChange);
    else doChange();
  };

  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

  const getFolderDepth = (folders, targetId, depth = 1) => {
    for (const f of folders) {
      if (f.id === targetId) return depth;
      if (f.children?.length) {
        const found = getFolderDepth(f.children, targetId, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const handleAddFolder = () => {
    const input = newFolderName.trim();
    if (!input) return;

    const parts = input.split("\\").map((p) => p.trim()).filter(Boolean);

    const selectedDepth = selectedFolderId
      ? (getFolderDepth(allFolders, selectedFolderId) ?? 0)
      : 0;
    const totalDepth = selectedDepth + parts.length;

    if (totalDepth > 4) {
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span style={{ marginLeft: 10, marginTop: 3 }}>
              Cannot add folder: maximum folder depth is 4 levels.
            </span>
          </div>
        </div>
      );
      setActiveImg(false);
      return;
    }

    const buildChain = (names) => {
      let node = { id: Date.now() + names.length, name: names[names.length - 1], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
      for (let i = names.length - 2; i >= 0; i--) {
        node = { id: Date.now() + i, name: names[i], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [node] };
      }
      return node;
    };

    const newNode = parts.length === 1
      ? { id: Date.now(), name: parts[0], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] }
      : buildChain(parts);

    if (!selectedFolderId) {
      setAllFolders((prev) => [...prev, newNode]);
    } else {
      const addChildRecursive = (folders) =>
        folders.map((f) => {
          if (f.id === selectedFolderId)
            return { ...f, children: [...(f.children || []), newNode] };
          if (f.children?.length)
            return { ...f, children: addChildRecursive(f.children) };
          return f;
        });
      setAllFolders((prev) => addChildRecursive(prev));
      setExpandedFolders((prev) => ({ ...prev, [selectedFolderId]: true }));
    }

    if (parts.length > 1) {
      const collectIds = (node) => {
        const ids = [node.id];
        node.children?.forEach((c) => ids.push(...collectIds(c)));
        return ids;
      };
      const allIds = collectIds(newNode);
      allIds.slice(0, -1).forEach((id) =>
        setExpandedFolders((prev) => ({ ...prev, [id]: true }))
      );
    }

    setNewFolderName("");
    setSelectedFolderId(null);
    setIsContentDirty(true);
  };

  const handleDeleteFolder = (id) => {
    const removeRecursive = (folders) =>
      folders
        .filter((f) => f.id !== id)
        .map((f) => ({ ...f, children: f.children ? removeRecursive(f.children) : [] }));
    setAllFolders((prev) => removeRecursive(prev));
    setFolderPdfs((prev) => { const n = { ...prev }; delete n[id]; return n; });
    setFolderYoutube((prev) => { const n = { ...prev }; delete n[id]; return n; });
    if (selectedFolderId === id) setSelectedFolderId(null);
    setIsContentDirty(true);
  };

  const handleSelectFolder = (id) => {
    setSelectedFolderId((prev) => (prev === id ? null : id));
  };

  const handleFolderPdfUpload = (id, files) => {
    const newPdfs = Array.from(files)
      .filter((f) => f.name.endsWith(".pdf"))
      .map((f) => ({ file: f, fileName: f.name, previewUrl: URL.createObjectURL(f) }));
    setFolderPdfs((prev) => ({ ...prev, [id]: [...(prev[id] || []), ...newPdfs] }));
    setIsContentDirty(true);
  };

  const clearFolderPdf = (id, idx) => {
    URL.revokeObjectURL(folderPdfs[id][idx].previewUrl);
    setFolderPdfs((prev) => ({ ...prev, [id]: prev[id].filter((_, i) => i !== idx) }));
    setIsContentDirty(true);
  };

  const toggleExpandFolder = (id) =>
    setExpandedFolders((p) => ({ ...p, [id]: !p[id] }));

  // ── Import checkbox handler ───────────────────────────────────────────────
  // The folder checkbox is DERIVED from the highlighted content: a folder is
  // checked while at least one PDF/YouTube item in it or in its sub-folders is
  // highlighted. Clicking a folder checkbox selects ALL / clears ALL content in
  // its subtree, then every folder (including all parents) is re-evaluated.
  const handleImportCheckbox = (folder, checked) => {
    const nextContent = new Set(importCheckedContentIds);
    subtreeContentKeys(folder, importFolderPdfs, importFolderYoutube).forEach((k) => {
      if (checked) nextContent.add(k);
      else nextContent.delete(k);
    });

    setImportCheckedContentIds(nextContent);
    setImportCheckedIds(
      computeCheckedFolderIds(importFolders, nextContent, importFolderPdfs, importFolderYoutube)
    );

    if (checked && (folder.children || []).length > 0) {
      const collectExpandIds = (node) => {
        const ids = [node.id];
        (node.children || []).forEach((c) => ids.push(...collectExpandIds(c)));
        return ids;
      };
      setImportExpandedFolders((prev) => {
        const next = { ...prev };
        collectExpandIds(folder).forEach((id) => { next[id] = true; });
        return next;
      });
    }
  };

  // ── Toggle a single content item highlight ───────────────────────────────
  // Chips can be freely selected / deselected. The folder (and its parents)
  // stay checked as long as something under them is still highlighted, and
  // only uncheck when the last highlighted chip is removed.
  const handleContentItemToggle = (folderId, type, index) => {
    const key = `${folderId}_${type}_${index}`;
    const nextContent = new Set(importCheckedContentIds);
    if (nextContent.has(key)) nextContent.delete(key);
    else nextContent.add(key);

    setImportCheckedContentIds(nextContent);
    setImportCheckedIds(
      computeCheckedFolderIds(importFolders, nextContent, importFolderPdfs, importFolderYoutube)
    );
  };

  // ── Close import modal helper ─────────────────────────────────────────────
  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setImportSelectedBatch(null);
    setImportFolders([]);
    setImportCheckedIds(new Set());
    setImportCheckedContentIds(new Set());
    setImportTargetFolderId(null);
    setImportContentWithFolder(true);
  };

  const openImportModal = () => {
    if (!(value.course_id && selectedSubject && selectedBatch)) {
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span style={{ marginLeft: 10, marginTop: 3 }}>
              Select Course, Subject and Batch first.
            </span>
          </div>
        </div>
      );
      setActiveImg(false);
      return;
    }
    setImportTargetFolderId(selectedFolderId);
    setImportCheckedIds(new Set());
    setImportCheckedContentIds(new Set());
    setIsImportModalOpen(true);
  };

  // ── Confirm import ────────────────────────────────────────────────────────
  const handleImportConfirm = () => {
    if (importCheckedIds.size === 0 && importCheckedContentIds.size === 0) return;

    const pdfAdds = {};
    const ytAdds = {};
    const expandAdds = {};
    let nextTree = null;

    const withContent = importContentWithFolder;
    const curFolderPdfs = importFolderPdfs;
    const curFolderYoutube = importFolderYoutube;
    const curCheckedIds = importCheckedIds;
    const curTargetId = importTargetFolderId;

    // Does this exact node have any of its own content highlighted (or is its
    // folder checkbox itself checked)? This is the real source of truth for
    // "should this node participate in import" — NOT curCheckedIds alone,
    // because deselecting one chip clears the folder checkbox without
    // clearing the other still-highlighted chips under that same folder.
    const nodeOwnSelected = (node) => {
      if (curCheckedIds.has(node.id)) return true;
      const pdfs = curFolderPdfs[node.id] || [];
      const yts = (curFolderYoutube[node.id] || {}).list || [];
      return (
        pdfs.some((_, i) => importCheckedContentIds.has(`${node.id}_pdf_${i}`)) ||
        yts.some((_, i) => importCheckedContentIds.has(`${node.id}_yt_${i}`))
      );
    };

    // Does this node OR any descendant have something selected?
    const nodeHasSelection = (node) => {
      if (nodeOwnSelected(node)) return true;
      return (node.children || []).some(nodeHasSelection);
    };

    // collect only highlighted content items
    const collectContent = (impNode, mainId) => {
      const pdfs = (curFolderPdfs[impNode.id] || [])
        .filter((_, i) => importCheckedContentIds.has(`${impNode.id}_pdf_${i}`))
        .map((p) => ({ fileName: p.fileName, previewUrl: p.previewUrl, pdfFileName: p.pdfFileName }));
      const allYts = (curFolderYoutube[impNode.id] || {}).list || [];
      const yts = allYts.filter((_, i) => importCheckedContentIds.has(`${impNode.id}_yt_${i}`));
      if (pdfs.length) pdfAdds[mainId] = [...(pdfAdds[mainId] || []), ...pdfs];
      if (yts.length) ytAdds[mainId] = [...(ytAdds[mainId] || []), ...yts];
    };

    // ── UNCHECKED + folder selected → content dump into target ────────────
    if (!withContent && curTargetId) {
      const walkForContent = (impNode) => {
        if (nodeOwnSelected(impNode)) {
          const pdfs = (curFolderPdfs[impNode.id] || [])
            .filter((_, i) => importCheckedContentIds.has(`${impNode.id}_pdf_${i}`))
            .map((p) => ({ fileName: p.fileName, previewUrl: p.previewUrl, pdfFileName: p.pdfFileName }));
          const allYts = (curFolderYoutube[impNode.id] || {}).list || [];
          const yts = allYts.filter((_, i) => importCheckedContentIds.has(`${impNode.id}_yt_${i}`));
          if (pdfs.length) pdfAdds[curTargetId] = [...(pdfAdds[curTargetId] || []), ...pdfs];
          if (yts.length) ytAdds[curTargetId] = [...(ytAdds[curTargetId] || []), ...yts];
          expandAdds[curTargetId] = true;
        }
        (impNode.children || []).forEach(walkForContent);
      };
      importFolders.forEach(walkForContent);

      setFolderPdfs((prev) => {
        const next = { ...prev };
        Object.entries(pdfAdds).forEach(([id, entries]) => {
          const existingKeys = new Set((next[id] || []).map((p) => p.pdfFileName));
          const deduped = entries.filter((p) => !existingKeys.has(p.pdfFileName));
          next[id] = [...(next[id] || []), ...deduped];
        });
        return next;
      });
      setFolderYoutube((prev) => {
        const next = { ...prev };
        Object.entries(ytAdds).forEach(([id, links]) => {
          const existing = next[id] || { list: [], url: "", error: "" };
          const existingSet = new Set(existing.list || []);
          const deduped = links.filter((l) => !existingSet.has(l));
          next[id] = { ...existing, list: [...(existing.list || []), ...deduped] };
        });
        return next;
      });
      setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
      setIsContentDirty(true);
      closeImportModal();
      return;
    }

    // ── UNCHECKED + no folder selected → folder structure only, no content ──
    if (!withContent && !curTargetId) {
      const tree = deepClone(allFolders);
      let seq = 0;
      const makeId = () => `imp-${Date.now()}-${seq++}`;

      const hasChecked = nodeHasSelection;

      const processNode = (impNode, siblings) => {
        if (!hasChecked(impNode)) return false;
        let mainNode;
        if (impNode.is_real) {
          mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
          if (!mainNode) {
            mainNode = { id: makeId(), name: impNode.name, is_real: true, isPendingNew: false, folderId: impNode.folderId, studyContentId: null, docs: [], videos: [], children: [] };
            siblings.push(mainNode);
          }
        } else {
          const nm = (impNode.name || "").trim().toLowerCase();
          mainNode = siblings.find((s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm);
          if (!mainNode) {
            mainNode = { id: makeId(), name: impNode.name, is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
            siblings.push(mainNode);
          }
        }
        if (!mainNode.children) mainNode.children = [];
        let childAdded = false;
        (impNode.children || []).forEach((c) => { if (processNode(c, mainNode.children)) childAdded = true; });
        if (childAdded) expandAdds[mainNode.id] = true;
        return true;
      };

      importFolders.forEach((f) => processNode(f, tree));
      setAllFolders(tree);
      setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
      setIsContentDirty(true);
      closeImportModal();
      return;
    }

    // ── CHECKED: folder + content import ─────────────────────────────────────
    if (curTargetId) {
      // ── MODE A ── checked + folder selected
      const tree = deepClone(allFolders);
      let seq = 0;
      const makeId = () => `imp-${Date.now()}-${seq++}`;

      const findNode = (nodes, id) => {
        for (const n of nodes) {
          if (n.id === id) return n;
          const found = findNode(n.children || [], id);
          if (found) return found;
        }
        return null;
      };

      const targetNode = findNode(tree, curTargetId);
      if (targetNode) {
        if (!targetNode.children) targetNode.children = [];

        const walkImport = (impNode, siblings) => {
          if (!nodeOwnSelected(impNode)) {
            (impNode.children || []).forEach((c) => walkImport(c, siblings));
            return;
          }
          if (impNode.is_real) {
            const mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
            if (mainNode) {
              collectContent(impNode, mainNode.id);
              expandAdds[curTargetId] = true;
              expandAdds[mainNode.id] = true;
              if (!mainNode.children) mainNode.children = [];
              (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
            } else {
              collectContent(impNode, curTargetId);
              expandAdds[curTargetId] = true;
              (impNode.children || []).forEach((c) => walkImport(c, siblings));
            }
            return;
          }
          const nm = (impNode.name || "").trim().toLowerCase();
          let mainNode = siblings.find((s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm);
          if (!mainNode) {
            mainNode = { id: makeId(), name: impNode.name, is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
            siblings.push(mainNode);
          }
          if (!mainNode.children) mainNode.children = [];
          collectContent(impNode, mainNode.id);
          expandAdds[curTargetId] = true;
          expandAdds[mainNode.id] = true;
          (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
        };

        importFolders.forEach((f) => walkImport(f, targetNode.children));
      }
      nextTree = tree;
    } else {
      // ── MODE B ── checked + no folder selected
      const tree = deepClone(allFolders);
      let seq = 0;
      const makeId = () => `imp-${Date.now()}-${seq++}`;

      const hasChecked = nodeHasSelection;

      const processNode = (impNode, siblings) => {
        if (!hasChecked(impNode)) return false;
        let mainNode;
        if (impNode.is_real) {
          mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
          if (!mainNode) {
            mainNode = { id: makeId(), name: impNode.name, is_real: true, isPendingNew: false, folderId: impNode.folderId, studyContentId: null, docs: [], videos: [], children: [] };
            siblings.push(mainNode);
          }
        } else {
          const nm = (impNode.name || "").trim().toLowerCase();
          mainNode = siblings.find((s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm);
          if (!mainNode) {
            mainNode = { id: makeId(), name: impNode.name, is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
            siblings.push(mainNode);
          }
        }
        if (!mainNode.children) mainNode.children = [];
        if (nodeOwnSelected(impNode)) collectContent(impNode, mainNode.id);
        let childAdded = false;
        (impNode.children || []).forEach((c) => { if (processNode(c, mainNode.children)) childAdded = true; });
        if (childAdded) expandAdds[mainNode.id] = true;
        return true;
      };

      importFolders.forEach((f) => processNode(f, tree));
      nextTree = tree;
    }

    if (nextTree) setAllFolders(nextTree);

    setFolderPdfs((prev) => {
      const next = { ...prev };
      Object.entries(pdfAdds).forEach(([id, entries]) => {
        const existingKeys = new Set((next[id] || []).map((p) => p.pdfFileName));
        const deduped = entries.filter((p) => !existingKeys.has(p.pdfFileName));
        next[id] = [...(next[id] || []), ...deduped];
      });
      return next;
    });
    setFolderYoutube((prev) => {
      const next = { ...prev };
      Object.entries(ytAdds).forEach(([id, links]) => {
        const existing = next[id] || { list: [], url: "", error: "" };
        const existingSet = new Set(existing.list || []);
        const deduped = links.filter((l) => !existingSet.has(l));
        next[id] = { ...existing, list: [...(existing.list || []), ...deduped] };
      });
      return next;
    });
    setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
    setIsContentDirty(true);
    closeImportModal();
  };

  // ── Folder row renderer ───────────────────────────────────────────────────
  const renderFolderRow = (folder, depth = 0) => {
    const isSelected = selectedFolderId === folder.id;
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders[folder.id] || false;

    return (
      <React.Fragment key={folder.id}>
        <tr style={{ height: 48 }}>
          <td style={{ paddingLeft: 36 + depth * 28, paddingTop: 12, paddingBottom: 12, verticalAlign: "middle" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {hasChildren ? (
                <span
                  style={{ cursor: "pointer", fontWeight: "bold", minWidth: 10, marginRight: 4 }}
                  onClick={() => toggleExpandFolder(folder.id)}
                >
                  {isExpanded ? "−" : "+"}
                </span>
              ) : (
                <span style={{ display: "inline-block", minWidth: 10, marginRight: 4 }} />
              )}
              <span
                onClick={() => handleSelectFolder(folder.id)}
                style={{
                  cursor: "pointer",
                  fontSize: 13,
                  padding: "3px 8px",
                  borderRadius: 4,
                  color: isSelected ? "#1a4d8f" : "rgb(66,66,66)",
                  fontWeight: isSelected ? 700 : 400,
                  background: isSelected ? "#c9e0ff" : "transparent",
                  transition: "background 0.15s",
                }}
                title="Click to select — then type a name above and click ADD SUB-FOLDER"
              >
                {folder.name}
              </span>
            </div>
          </td>
          <td style={{ verticalAlign: "middle", paddingTop: 12, paddingBottom: 12, paddingRight: 12, paddingLeft: 60 }}>
            {renderUploadYoutubeRow({
              id: folder.id,
              type: "folder",
              pdfs: folderPdfs,
              onPdfUpload: handleFolderPdfUpload,
              onPdfClear: clearFolderPdf,
              youtube: folderYoutube,
              onYoutubeChange: async (id, url) => {
                setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], url, error: "", urlTitle: "" } }));
                const vid = getYoutubeVideoId(url.trim());
                if (!vid) return;
                if (ytTitleCacheRef.current[url]) {
                  setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], urlTitle: ytTitleCacheRef.current[url] } }));
                  return;
                }
                try {
                  const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
                  if (res.ok) {
                    const data = await res.json();
                    ytTitleCacheRef.current[url] = data.title;
                    setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], urlTitle: data.title } }));
                  }
                } catch { /* ignore */ }
              },
              onYoutubeAdd: (id) => {
                const url = (folderYoutube[id]?.url || "").trim();
                if (!url) {
                  setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], error: "Please enter a YouTube link." } }));
                  return;
                }
                if (!getYoutubeVideoId(url)) {
                  setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], error: "Invalid YouTube URL." } }));
                  return;
                }
                setFolderYoutube((prev) => {
                  const existing = prev[id] || {};
                  const list = [...(existing.list || []), url];
                  return { ...prev, [id]: { ...existing, list, url: "", error: "", urlTitle: "" } };
                });
                setIsContentDirty(true);
              },
              onYoutubeClear: (id, idx) => {
                setFolderYoutube((prev) => {
                  const existing = prev[id] || {};
                  const list = (existing.list || []).filter((_, i) => i !== idx);
                  return { ...prev, [id]: { ...existing, list } };
                });
                setIsContentDirty(true);
              },
              onYoutubePlay: (link) => {
                openYoutubeModal(link);
              },
            })}
          </td>
          <td style={{ width: 50, textAlign: "center", verticalAlign: "middle" }}>
            {!folder.is_real && (
              <button
                type="button"
                onClick={() => handleDeleteFolder(folder.id)}
                style={{ background: "transparent", border: "none", color: "#e53935", cursor: "pointer", fontSize: 15, fontWeight: 700, padding: 0, marginLeft: "460px" }}
                title="Delete folder"
              >✕</button>
            )}
          </td>
        </tr>
        {isExpanded && hasChildren && folder.children.map((child) => renderFolderRow(child, depth + 1))}
        {isExpanded && !hasChildren && (
          <tr>
            <td
              colSpan={3}
              style={{ paddingLeft: 60 + depth * 28, fontSize: 12, color: "#aaa", paddingTop: 4, paddingBottom: 4 }}
            >
              No sub-folders
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const accessToken = localStorage.getItem("other");

  useEffect(() => { dispatch(fetchCourseData()); }, [dispatch]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (value.course_id) {
        try {
          const res = await fetch(`${FULL_API_BASE_URL}/get-subjects-by-course/?course_id=${value.course_id}`);
          const data = await res.json();
          setSubjects(data);
        } catch (e) { console.error(e); }
        setSelectedSubject(null);
        setSelectedSubjectLabel("");
      }
    };
    fetchSubjects();
  }, [value.course_id]);

  useEffect(() => {
    const fetchBatches = async () => {
      const res = await fetch(`${FULL_API_BASE_URL}/get-batches/`);
      const data = await res.json();
      setBatches(data.data);
    };
    fetchBatches();
  }, []);

  const fetchStudyContent = useCallback(async (signal) => {
    if (!(value.course_id && selectedSubject && selectedBatch)) {
      setAllFolders([]);
      return;
    }

    setIsFoldersLoading(true);
    setFoldersError("");

    try {
      const url =
        `${FULL_API_BASE_URL}/get-study-content/` +
        `?course_id=${value.course_id}` +
        `&subject_id=${selectedSubject}` +
        `&batch_id=${selectedBatch.batch_id}`;

      const res = await fetch(url, {
        headers: {
          accept: "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        signal,
      });

      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

      const json = await res.json();
      if (json?.status !== 200) throw new Error(json?.message || "Failed to load study content.");

      const chapters = json?.data?.chapters || [];
      const customFolders = json?.data?.custom_folders || [];

      const chapterTree = chapters.map((c) => mapApiFolderToTree(c));
      const customTree = customFolders.map((c) => mapApiFolderToTree(c, "custom"));
      const combinedTree = [...chapterTree, ...customTree];

      setAllFolders(combinedTree);
      originalFoldersSnapshotRef.current = combinedTree;

      const pdfsMap = {};
      const ytMap = {};
      const pdfBaseline = {};
      const ytBaseline = {};
      seedContentState(combinedTree, pdfsMap, ytMap, pdfBaseline, ytBaseline);
      setFolderPdfs(pdfsMap);
      setFolderYoutube(ytMap);

      Object.values(ytMap).forEach(({ list }) => {
        (list || []).forEach(async (url) => {
          if (!url || ytTitleCacheRef.current[url]) return;
          try {
            const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
            if (res.ok) {
              const data = await res.json();
              ytTitleCacheRef.current[url] = data.title;
              setFolderYoutube((prev) => ({ ...prev }));
            }
          } catch { /* ignore */ }
        });
      });

      folderPdfsBaselineRef.current = pdfBaseline;
      folderYoutubeBaselineRef.current = ytBaseline;
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error("Failed to fetch study content:", e);
      setFoldersError("Could not load folders. Please try again.");
      setAllFolders([]);
      originalFoldersSnapshotRef.current = [];
      setFolderPdfs({});
      setFolderYoutube({});
      folderPdfsBaselineRef.current = {};
      folderYoutubeBaselineRef.current = {};
    } finally {
      setIsFoldersLoading(false);
    }

    setExpandedFolders({});
    setSelectedFolderId(null);
    setNewFolderName("");
    setIsContentDirty(false);
  }, [value.course_id, selectedSubject, selectedBatch, accessToken]);

  useEffect(() => {
    const controller = new AbortController();
    fetchStudyContent(controller.signal);
    return () => controller.abort();
  }, [fetchStudyContent]);

  // ── Import modal: fetch when batch changes ────────────────────────────────
  useEffect(() => {
    if (!importSelectedBatch || !value.course_id || !selectedSubject) {
      setImportFolders([]);
      setImportFolderPdfs({});
      setImportFolderYoutube({});
      setImportExpandedFolders({});
      return;
    }

    const controller = new AbortController();

    (async () => {
      setIsImportFoldersLoading(true);
      setImportFoldersError("");
      try {
        const url =
          `${FULL_API_BASE_URL}/get-study-content/` +
          `?course_id=${value.course_id}` +
          `&subject_id=${selectedSubject}` +
          `&batch_id=${importSelectedBatch.batch_id}`;

        const res = await fetch(url, {
          headers: { accept: "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`Status ${res.status}`);
        const json = await res.json();
        if (json?.status !== 200) throw new Error(json?.message || "Failed");

        const chapters = json?.data?.chapters || [];
        const customFolders = json?.data?.custom_folders || [];
        const combined = [
          ...chapters.map((c) => mapApiFolderToTree(c)),
          ...customFolders.map((c) => mapApiFolderToTree(c, "custom")),
        ];

        setImportFolders(combined);

        const pdfsMap = {};
        const ytMap = {};
        const _pb = {}; const _yb = {};
        seedContentState(combined, pdfsMap, ytMap, _pb, _yb);

        Object.values(ytMap).forEach(({ list }) => {
          (list || []).forEach(async (url) => {
            if (!url || ytTitleCacheRef.current[url]) return;
            try {
              const r = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
              if (r.ok) {
                const d = await r.json();
                ytTitleCacheRef.current[url] = d.title;
                setImportFolderYoutube((p) => ({ ...p }));
              }
            } catch { /* ignore */ }
          });
        });

        setImportFolderPdfs(pdfsMap);
        setImportFolderYoutube(ytMap);
        setImportExpandedFolders({});
        setImportCheckedIds(new Set());
        setImportCheckedContentIds(new Set());
      } catch (e) {
        if (e.name === "AbortError") return;
        setImportFoldersError("Could not load folders.");
        setImportFolders([]);
      } finally {
        setIsImportFoldersLoading(false);
      }
    })();

    return () => controller.abort();
  }, [importSelectedBatch]);

  // ── YouTube helpers ───────────────────────────────────────────────────────
  const openYoutubeModal = (url) => {
    const vid = getYoutubeVideoId(url);
    if (!vid) return null;
    setYtVideoId(vid);
    setIsYoutubeModalOpen(true);
    return vid;
  };

  const clearPlayerTimers = () => {
    clearInterval(timeUpdaterRef.current);
    clearTimeout(idleTimerRef.current);
  };

  const closeYoutubeModal = useCallback(() => {
    clearPlayerTimers();
    if (playerRef.current) {
      try { playerRef.current.destroy(); } catch { /* ignore */ }
      playerRef.current = null;
    }
    setIsKioskFS(false);
    setUiHidden(false);
    setIsPlaying(false);
    setProgressVal(0);
    setCurrentTimeFmt("0:00");
    setTotalTimeFmt("0:00");
    setIsYoutubeModalOpen(false);
    setYtVideoId(null);
  }, []);

  const showUI = useCallback(() => {
    setUiHidden(false);
    clearTimeout(idleTimerRef.current);
  }, []);

  const resetIdleTimer = useCallback(() => {
    showUI();
    clearTimeout(idleTimerRef.current);
    if (playerRef.current?.getPlayerState?.() === 1) {
      idleTimerRef.current = setTimeout(() => setUiHidden(true), 3500);
    }
  }, [showUI]);

  useEffect(() => {
    if (!isYoutubeModalOpen || !ytVideoId) return;

    setIsPlaying(false); setProgressVal(0);
    setCurrentTimeFmt("0:00"); setTotalTimeFmt("0:00");
    setUiHidden(false); setSpeed(1);

    onYTReady(() => {
      if (!ytDivRef.current) return;
      if (playerRef.current) { try { playerRef.current.destroy(); } catch { /* ignore */ } }

      playerRef.current = new window.YT.Player(ytDivRef.current, {
        videoId: ytVideoId,
        playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, playsinline: 1 },
        events: {
          onReady: (e) => {
            const dur = e.target.getDuration();
            setDurationVal(dur);
            setTotalTimeFmt(formatTime(dur));
            resetIdleTimer();
          },
          onStateChange: (e) => {
            const YT = window.YT.PlayerState;
            if (e.data === YT.PLAYING) {
              setIsPlaying(true);
              resetIdleTimer();
              clearInterval(timeUpdaterRef.current);
              timeUpdaterRef.current = setInterval(() => {
                const ct = playerRef.current?.getCurrentTime?.() || 0;
                const dur = playerRef.current?.getDuration?.() || 0;
                setProgressVal(ct);
                setCurrentTimeFmt(formatTime(ct));
                if (dur) setTotalTimeFmt(formatTime(dur));
              }, 500);
            } else {
              setIsPlaying(false);
              showUI();
              clearInterval(timeUpdaterRef.current);
              clearTimeout(idleTimerRef.current);
            }
            if (e.data === YT.ENDED) {
              playerRef.current?.seekTo(0);
              playerRef.current?.pauseVideo();
            }
          },
        },
      });
    });

    return () => clearPlayerTimers();
  }, [isYoutubeModalOpen, ytVideoId]);

  useEffect(() => () => {
    clearPlayerTimers();
    playerRef.current?.destroy?.();
  }, []);

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p) return;
    p.getPlayerState() === 1 ? p.pauseVideo() : p.playVideo();
    resetIdleTimer();
  };

  const handleSeek = (e) => {
    const val = parseFloat(e.target.value);
    playerRef.current?.seekTo(val, true);
    setProgressVal(val);
    resetIdleTimer();
  };

  const handleSpeed = (e) => {
    const val = parseFloat(e.target.value);
    playerRef.current?.setPlaybackRate(val);
    setSpeed(val);
    resetIdleTimer();
  };

  const handleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    const isFS = document.fullscreenElement || document.webkitFullscreenElement || isKioskFS;
    if (!isFS) {
      if (el.requestFullscreen) {
        el.requestFullscreen()
          .then(() => window.screen.orientation?.lock?.("landscape").catch(() => {}))
          .catch(() => { setIsKioskFS(true); window.screen.orientation?.lock?.("landscape").catch(() => {}); });
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
        setIsKioskFS(true);
        window.screen.orientation?.lock?.("landscape").catch(() => {});
      } else {
        setIsKioskFS(true);
        window.screen.orientation?.lock?.("landscape").catch(() => {});
      }
    } else {
      setIsKioskFS(false);
      window.screen.orientation?.unlock?.();
      document.exitFullscreen?.().catch(() => {});
      document.webkitExitFullscreen?.();
    }
  };

  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setIsKioskFS(false);
        window.screen.orientation?.unlock?.();
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isContentDirty) {
      setIsMessageBoxVisible(true);
      setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>Nothing to save.</span></div></div>);
      setActiveImg(false);
      return;
    }
    if (!(value.course_id && selectedSubject && selectedBatch)) {
      setIsMessageBoxVisible(true);
      setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>Select Course, Subject and Batch first.</span></div></div>);
      setActiveImg(false);
      return;
    }

    setIsSaving(true);
    const uploadedBy = userName;

    try {
      const currentIds = new Set();
      collectStudyContentIds(allFolders, currentIds);
      const deletedIds = [];
      collectDeletedRoots(originalFoldersSnapshotRef.current, currentIds, deletedIds);

      for (const id of deletedIds) {
        await saveStudyContentBatch({
          uploadedBy,
          item: { op: "delete", study_content_id: id },
          accessToken,
        });
      }

      const walk = async (node, ancestorPath) => {
        let ownId = node.studyContentId;
        const ownIsReal = node.is_real;

        const pdfList = folderPdfs[node.id] || [];
        const newPdfFiles = pdfList.filter((p) => p.file).map((p) => p.file);
        const currentPdfKeys = pdfList.filter((p) => p.pdfFileName).map((p) => p.pdfFileName);
        const baselinePdfKeys = folderPdfsBaselineRef.current[node.id] || [];
        const removedPdfKeys = diffList(currentPdfKeys, baselinePdfKeys).removed;

        const ytData = folderYoutube[node.id] || {};
        const currentYtLinks = ytData.list || [];
        const baselineYtLinks = folderYoutubeBaselineRef.current[node.id] || [];
        const { added: newYtLinks, removed: removedYtLinks } = diffList(currentYtLinks, baselineYtLinks);

        const needsChange = newPdfFiles.length || removedPdfKeys.length || newYtLinks.length || removedYtLinks.length;

        if (node.isPendingNew) {
          const { chapter_id, topic_id } = pinnedFieldsFromPath(ancestorPath);
          const item = {
            op: "create",
            batch_id: selectedBatch.batch_id,
            subject_id: selectedSubject,
            chapter_id,
            topic_id,
            folder_path: [
              ...ancestorPath.map((p) => ({ id: p.id, name: p.name })),
              { id: null, name: node.name },
            ],
          };
          if (newYtLinks.length) item.add_yt_video_urls = newYtLinks;

          const result = await saveStudyContentBatch({
            uploadedBy,
            item,
            pdfFiles: newPdfFiles,
            accessToken,
          });
          ownId = result.study_content_id;
        } else if (!ownId && needsChange) {
          const selfEntry = { id: node.folderId, name: node.name, is_real: node.is_real };
          const fullPath = [...ancestorPath, selfEntry];
          const { chapter_id, topic_id } = pinnedFieldsFromPath(fullPath);
          const item = {
            op: "create",
            batch_id: selectedBatch.batch_id,
            subject_id: selectedSubject,
            chapter_id,
            topic_id,
            folder_path: fullPath.map((p) => ({ id: p.id, name: p.name })),
          };
          if (newYtLinks.length) item.add_yt_video_urls = newYtLinks;

          await saveStudyContentBatch({ uploadedBy, item, pdfFiles: newPdfFiles, accessToken });
          ownId = node.folderId;
        } else if (ownId && needsChange) {
          const item = { op: "update", study_content_id: ownId };
          if (newYtLinks.length) item.add_yt_video_urls = newYtLinks;
          if (removedYtLinks.length) item.remove_yt_video_urls = removedYtLinks;
          if (removedPdfKeys.length) item.remove_pdf_keys = removedPdfKeys;

          await saveStudyContentBatch({ uploadedBy, item, pdfFiles: newPdfFiles, accessToken });
        }

        const pathId = node.isPendingNew ? ownId : (ownIsReal ? node.folderId : ownId);
        const nextAncestorPath = [...ancestorPath, { id: pathId, name: node.name, is_real: ownIsReal }];
        for (const child of (node.children || [])) {
          await walk(child, nextAncestorPath);
        }
      };

      for (const root of allFolders) {
        await walk(root, []);
      }

      setIsMessageBoxVisible(true);
      setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>All changes saved successfully.</span></div></div>);
      setActiveImg(false);

      await fetchStudyContent();
    } catch (e) {
      console.error(e);
      setIsMessageBoxVisible(true);
      setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>Could not save everything: {e.message}</span></div></div>);
      setActiveImg(false);
      await fetchStudyContent();
    } finally {
      setIsSaving(false);
    }
  };

  // ── renderUploadYoutubeRow ────────────────────────────────────────────────
  const renderUploadYoutubeRow = ({ id, type, pdfs, onPdfUpload, onPdfClear, youtube, onYoutubeChange, onYoutubeAdd, onYoutubeClear, onYoutubePlay }) => {
    const pdfList = pdfs[id] || [];
    const ytData = youtube[id] || {};
    const ytList = ytData.list || [];
    const inputId = `pdf-input-${type}-${id}`;

    const renderPdfName = (pdf, index) => (
      <div style={{ position: "relative", width: 280, flexShrink: 0 }}>
        <span
          style={{
            display: "block", fontSize: 11, color: "#333", background: "#eef4ff",
            border: "1px solid #c9e0ff", borderRadius: 4, padding: "3px 48px 3px 8px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            height: 22, boxSizing: "border-box", lineHeight: "15px",
          }}
        >
          {pdf.fileName}
        </span>
        <div
          style={{
            position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)",
            display: "flex", alignItems: "center", gap: 2, background: "#eef4ff", paddingLeft: 2,
          }}
        >
          <button
            type="button"
            onClick={() => { setModalPdfUrl(pdf.previewUrl); setModalPdfName(pdf.fileName); setIsPdfModalOpen(true); }}
            style={{ width: 18, height: 18, padding: 0, background: "transparent", color: "#2e7d32", border: "none", cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            title="View PDF"
          >👁</button>
          <button
            type="button"
            onClick={() => onPdfClear(id, index)}
            style={{ width: 18, height: 18, padding: 0, background: "transparent", color: "#e53935", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            title="Remove PDF"
          >✕</button>
        </div>
      </div>
    );

    const renderYoutubeChip = (link, index) => {
      const chipTitle = ytTitleCacheRef.current[link] || link;
      return (
        <div style={{ position: "relative", width: 280, flexShrink: 0 }}>
          <span
            style={{
              display: "block", fontSize: 11, color: "#333",
              border: "1px solid rgb(204, 204, 204)", borderRadius: 4, padding: "3px 48px 3px 8px",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              height: 22, boxSizing: "border-box", lineHeight: "15px",
            }}
            title={chipTitle}
          >
            {chipTitle}
          </span>
          <div
            style={{
              position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)",
              display: "flex", alignItems: "center", gap: 2, background: "#fff0f0", paddingLeft: 2,
            }}
          >
            <button
              type="button"
              onClick={() => onYoutubePlay(link)}
              style={{ width: 18, height: 18, padding: 0, color: "#e50914", border: "none", cursor: "pointer", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              title="Play YouTube"
            >▶</button>
            <button
              type="button"
              onClick={() => onYoutubeClear(id, index)}
              style={{ width: 18, height: 18, padding: 0, color: "#e53935", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              title="Remove link"
            >✕</button>
          </div>
        </div>
      );
    };

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, flexWrap: "nowrap", marginLeft: "365px" }}>
          <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3 }}>
            {pdfList.map((pdf, idx) => (
              <div key={idx}>{renderPdfName(pdf, idx)}</div>
            ))}
          </div>
          <label htmlFor={inputId} className="browse-button-study-content" title="Upload PDF">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <polyline points="9 15 12 12 15 15" />
            </svg>
            <span style={{ fontSize: 7, fontWeight: 700, letterSpacing: 0.3, lineHeight: 1 }}>PDF</span>
          </label>
          <input
            id={inputId} type="file" accept=".pdf" multiple style={{ display: "none" }}
            onChange={(e) => { onPdfUpload(id, e.target.files); e.target.value = ""; }}
          />
          <div style={{ width: 96, flexShrink: 0 }} />
          <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3 }}>
            {ytList.map((link, idx) => (
              <div key={idx}>{renderYoutubeChip(link, idx)}</div>
            ))}
          </div>
          <div style={{ position: "relative", flexShrink: 0, marginRight: "180" }}>
            <input
              type="text"
              value={ytData.url || ""}
              onChange={(e) => onYoutubeChange(id, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onYoutubeAdd(id); }}
              placeholder="Enter YouTube video URL"
              style={{ width: 160, flexShrink: 0, padding: "4px 10px", border: `1px solid ${ytData.error ? "#e53935" : "#ccc"}`, borderRadius: 5, fontSize: 12, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
            />
            {ytData.urlTitle && (
              <span style={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                fontSize: 10, color: "#4caf50", pointerEvents: "none",
              }}>✓</span>
            )}
          </div>
        </div>
        {ytData.error && (
          <span style={{ fontSize: 11, color: "#e53935", display: "block", paddingLeft: 490, whiteSpace: "nowrap" }}>
            ⚠ {ytData.error}
          </span>
        )}
      </div>
    );
  };

  const ctrlBtn = {
    background: "#e50914", color: "#fff", border: "none",
    minWidth: 44, height: 38, padding: "0 12px",
    borderRadius: 6, cursor: "pointer", fontWeight: "bold", fontSize: 14,
  };

  // ── Import folder row renderer (highlight only, no inner checkboxes) ──────
  const renderImportFolderRow = (folder, depth = 0) => {
    const isExpanded = importExpandedFolders[folder.id] || false;
    const pdfList = importFolderPdfs[folder.id] || [];
    const ytList = (importFolderYoutube[folder.id] || {}).list || [];
    const hasContent = pdfList.length > 0 || ytList.length > 0;
    const childrenWithContent = (folder.children || []).filter((c) =>
      hasAnyContent(c, importFolderPdfs, importFolderYoutube)
    );
    const hasChildren = childrenWithContent.length > 0;

    if (!hasContent && !hasChildren) return null;

    const isFolderChecked = importCheckedIds.has(folder.id);

    const chipBaseStyle = {
      display: "block",
      fontSize: 11,
      borderRadius: 4,
      padding: "3px 8px",
      boxSizing: "border-box",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      cursor: "pointer",
      transition: "background 0.15s, border-color 0.15s, color 0.15s",
      userSelect: "none",
    };

    const pdfChipStyle = (isSelected) => ({
      ...chipBaseStyle,
      width: 220,
      color: isSelected ? "#1a4d8f" : "#333",
      background: isSelected ? "#ddeeff" : "transparent",
      border: `1px solid ${isSelected ? "#90bfff" : "#ccc"}`,
    });

    const ytChipStyle = (isSelected) => ({
      ...chipBaseStyle,
      width: 260,
      color: isSelected ? "#1a4d8f" : "#333",
      background: isSelected ? "#ddeeff" : "transparent",
      border: `1px solid ${isSelected ? "#90bfff" : "#ccc"}`,
    });

    return (
      <React.Fragment key={folder.id}>
        <tr style={{ height: 40 }}>
          <td style={{ paddingLeft: 24 + depth * 32, verticalAlign: "baseline", width: 310 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                className="delete-question-checkbox-dqc"
                checked={isFolderChecked}
                onChange={(e) => handleImportCheckbox(folder, e.target.checked)}
                style={{ flexShrink: 0 }}
              />
              <span
                style={{ cursor: hasChildren ? "pointer" : "default", fontWeight: "bold", minWidth: 15, flexShrink: 0 }}
                onClick={() => hasChildren && setImportExpandedFolders((p) => ({ ...p, [folder.id]: !p[folder.id] }))}
              >
                {hasChildren ? (isExpanded ? "−" : "+") : ""}
              </span>
              <span style={{ fontSize: 13 }}>
                {folder.name}
              </span>
            </div>
          </td>

          <td style={{ verticalAlign: "baseline", paddingTop: 8, paddingBottom: 8, paddingLeft: 189, width: 200 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {pdfList.map((pdf, i) => {
                const key = `${folder.id}_pdf_${i}`;
                const isSelected = importCheckedContentIds.has(key);
                return (
                  <span
                    key={i}
                    style={pdfChipStyle(isSelected)}
                    title={pdf.fileName}
                    onClick={() => handleContentItemToggle(folder.id, "pdf", i)}
                  >
                    {pdf.fileName}
                  </span>
                );
              })}
            </div>
          </td>

          <td style={{ verticalAlign: "baseline", paddingTop: 8, paddingBottom: 8, paddingLeft: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {ytList.map((link, i) => {
                const key = `${folder.id}_yt_${i}`;
                const isSelected = importCheckedContentIds.has(key);
                const title = ytTitleCacheRef.current[link] || link;
                return (
                  <span
                    key={i}
                    style={ytChipStyle(isSelected)}
                    title={title}
                    onClick={() => handleContentItemToggle(folder.id, "yt", i)}
                  >
                    {title}
                  </span>
                );
              })}
            </div>
          </td>
        </tr>

        {isExpanded && childrenWithContent.map((c) => renderImportFolderRow(c, depth + 1))}
      </React.Fragment>
    );
  };

  const importTargetPath = importTargetFolderId
    ? getFolderPath(allFolders, importTargetFolderId)
    : null;

  // Enabled / count for the IMPORT button: a folder checkbox being unchecked
  // doesn't mean nothing will import — individual highlighted chips still
  // count. importCheckedContentIds is the true count of items that will
  // actually be imported.
  const hasImportSelection = importCheckedIds.size > 0 || importCheckedContentIds.size > 0;

  return (
    <div className="create-role-container">
      <div className="create-role-container-ques-paper-del">
        <span className="create-role-heading">Study Resources</span>
        <div className="cr-header-btn-container">
          <button className="cr-cancel-btn">CANCEL</button>
          <button className="cr-cancel-btn" onClick={openImportModal}>IMPORT</button>
          <button className="cr-create-btn" onClick={handleSave} disabled={isSaving}>SAVE</button>
        </div>
      </div>

      {/* SECTION 1: BATCH DETAILS */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header"><label>BATCH DETAILS</label></div>
          <div className="accordion-btn" onClick={() => setIsOpenSubject((p) => !p)}>
            {isOpenSubject ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenSubject && (
          <div className="accordion-content">
            <div className="select-assign-test-container-ques-pap-del">
              <div className="grid-container-ques-paper-del">
                <div className="item1">
                  <DropDown
                    label="Course"
                    setSelected={handleCourseChange}
                    options={coursesdata ? coursesdata.map((c) => ({ label: c.course_description, id: c.course_id })) : []}
                    isRequired={true}
                  />
                </div>
                <div className="item2">
                  <DropDown
                    label="Subject"
                    setSelected={handleSubjectChange}
                    selectedLabel={selectedSubjectLabel}
                    setSelectedLabel={setSelectedSubjectLabel}
                    options={subjects.map((s) => ({ label: s.subject_name, id: s.subject_id }))}
                    isRequired={true}
                  />
                </div>
                <div className="item3">
                  <DropDown
                    label="Batch Name"
                    style={{ width: "100%" }}
                    selected={selectedBatch ? selectedBatch.batch_id : null}
                    setSelected={handleBatchSelection}
                    options={batches.map((b) => ({ id: b.batch_id, label: b.batch_name }))}
                    isRequired={true}
                    Height="auto"
                    minheight="200px"
                    OverFlow="auto"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── PDF MODAL ── */}
      {isPdfModalOpen && (
        <div
          onClick={() => { setIsPdfModalOpen(false); setModalPdfUrl(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "min(1200px, 95vw)" }}>
            <button
              type="button"
              onClick={() => { setIsPdfModalOpen(false); setModalPdfUrl(null); }}
              style={{ position: "absolute", top: -10, right: -58, background: "transparent", border: "none", color: "#fff", width: 36, height: 36, fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
            >✕</button>
            <div style={{ borderRadius: 10, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}>
              <iframe
                src={`${modalPdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                title="PDF Preview"
                width="100%"
                height="760px"
                style={{ border: "none", display: "block" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── YOUTUBE PLAYER MODAL ── */}
      {isYoutubeModalOpen && (
        <div
          onClick={closeYoutubeModal}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }}
        >
          <div
            ref={containerRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: isKioskFS ? "fixed" : "relative",
              ...(isKioskFS
                ? { inset: 0, width: "100vw", height: "100dvh", maxWidth: "none", borderRadius: 0, zIndex: 999999 }
                : { width: "min(860px, 95vw)", borderRadius: 10 }),
              background: "#000", overflow: "hidden",
              boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
              display: "flex", flexDirection: "column",
            }}
          >
            <button
              type="button"
              onClick={closeYoutubeModal}
              onMouseMove={resetIdleTimer}
              style={{
                position: "absolute", top: 15, left: 15, zIndex: 40,
                width: 44, height: 44, background: "rgba(0,0,0,0.65)",
                border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                opacity: uiHidden ? 0 : 1,
                visibility: uiHidden ? "hidden" : "visible",
                transition: "opacity 0.3s, visibility 0.3s",
                pointerEvents: uiHidden ? "none" : "auto",
              }}
              title="Close"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>

            <div style={{
              position: "absolute", top: 5, right: 5, zIndex: 30,
              pointerEvents: "none",
              opacity: uiHidden ? 0.2 : 0.85,
              transition: "opacity 0.3s",
            }}>
              <span style={{ color: "#ff0000", fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>▶ DSE</span>
            </div>

            <div
              style={{ position: "relative", paddingBottom: isKioskFS ? 0 : "56.25%", height: isKioskFS ? 0 : 0, flexGrow: isKioskFS ? 1 : 0, overflow: "hidden", background: "#000" }}
              onMouseMove={resetIdleTimer}
            >
              <div
                ref={ytDivRef}
                style={{ position: "absolute", top: -80, left: 0, width: "100%", height: "calc(100% + 220px)", pointerEvents: "none" }}
              />
              <div
                style={{ position: "absolute", inset: 0, zIndex: 20, cursor: "pointer" }}
                onClick={() => {
                  if (uiHidden) { resetIdleTimer(); }
                  else { togglePlay(); resetIdleTimer(); }
                }}
              />
            </div>

            <div
              onMouseMove={resetIdleTimer}
              style={{
                ...(isKioskFS
                  ? { position: "absolute", bottom: 0, left: 0, width: "100%", background: "rgba(24,24,24,0.85)" }
                  : { background: "#181818" }),
                display: "flex", flexWrap: "wrap", alignItems: "center",
                padding: "12px 16px", gap: 12,
                opacity: uiHidden ? 0 : 1,
                visibility: uiHidden ? "hidden" : "visible",
                transition: "opacity 0.3s, visibility 0.3s",
                pointerEvents: uiHidden ? "none" : "auto",
                zIndex: 40,
              }}
            >
              <input
                type="range" min={0} max={durationVal || 100} step={0.1} value={progressVal}
                onChange={handleSeek}
                style={{ flexBasis: "100%", order: -1, height: 6, accentColor: "#e50914", cursor: "pointer", marginBottom: 4 }}
              />
              <button type="button" style={ctrlBtn} onClick={() => { togglePlay(); resetIdleTimer(); }}>
                {isPlaying ? "Pause" : "Play"}
              </button>
              <span style={{ color: "#ccc", fontSize: 13, minWidth: 75, textAlign: "center" }}>
                {currentTimeFmt} / {totalTimeFmt}
              </span>
              <select
                value={speed} onChange={handleSpeed}
                style={{ background: "#2a2a2a", color: "#fff", border: "1px solid #444", height: 38, padding: "0 8px", borderRadius: 6, fontSize: 13 }}
              >
                <option value={1}>1x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>
              <button type="button" style={{ ...ctrlBtn, marginLeft: "auto" }} onClick={handleFullscreen}>
                {isKioskFS || document.fullscreenElement ? "Exit" : "Full"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTENT FOLDER SECTION */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header"><label>CONTENT FOLDER</label></div>
          <div className="accordion-btn" onClick={() => setIsOpenCustomFolder((p) => !p)}>
            {isOpenCustomFolder ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenCustomFolder && (
          <div className="accordion-content">
            <div style={{ padding: "10px 16px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAddFolder(); }}
                  placeholder={selectedFolderId ? "Enter sub-folder name..." : "Enter folder name..."}
                  style={{ flex: 1, maxWidth: 320, padding: "6px 12px", border: "1px solid #ccc", borderRadius: 5, fontSize: 13, outline: "none", color: "rgb(66,66,66)", transition: "border 0.2s" }}
                />
                <button
                  type="button"
                  onClick={handleAddFolder}
                  style={{ padding: "6px 18px", background: "#2F2E40", color: "#fff", border: "none", borderRadius: 5, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  ADD FOLDER
                </button>
              </div>
              {selectedFolderId && (() => {
                const path = getFolderPath(allFolders, selectedFolderId);
                return path ? (
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                    {path.map((segment, i) => (
                      <React.Fragment key={i}>
                        <span style={{ fontSize: 12, color: "#555" }}>{segment}</span>
                        <span style={{ fontSize: 12, color: "#aaa", margin: "0 2px" }}>\</span>
                      </React.Fragment>
                    ))}
                    {newFolderName.trim() && (
                      <>
                        <span style={{ fontSize: 12, color: "#555" }}>{newFolderName}</span>
                        <span style={{ fontSize: 12, color: "#aaa", margin: "0 2px" }}>\</span>
                      </>
                    )}
                  </div>
                ) : null;
              })()}
            </div>

            {isFoldersLoading && (
              <div style={{ padding: "10px 16px", fontSize: 13, color: "#888" }}>Loading folders...</div>
            )}
            {!isFoldersLoading && foldersError && (
              <div style={{ padding: "10px 16px", fontSize: 13, color: "#e53935" }}>⚠ {foldersError}</div>
            )}
            {!isFoldersLoading && !foldersError && allFolders.length > 0 && (
              <table className="table-tc-create-test" style={{ opacity: 0.8, width: "100%", tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <td style={{ paddingLeft: 24, borderBottom: "1px solid #e0e0e0" }}>Folder Name</td>
                    <td style={{ paddingLeft: 310, borderBottom: "1px solid #e0e0e0" }}>PDF</td>
                    <td style={{ paddingLeft: 232, borderBottom: "1px solid #e0e0e0" }}>YouTube</td>
                    <td style={{ width: 50, borderBottom: "1px solid #e0e0e0" }}></td>
                  </tr>
                </thead>
                <tbody>
                  {allFolders.map((folder) => renderFolderRow(folder, 0))}
                </tbody>
              </table>
            )}
            {!isFoldersLoading && !foldersError && allFolders.length === 0 && (
              <div style={{ padding: "10px 16px", fontSize: 13, color: "#bbb" }}>
                {value.course_id && selectedSubject && selectedBatch
                  ? "No folders for this selection. Add one above."
                  : "Select Course, Subject and Batch to load folders, or add one manually."}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── IMPORT MODAL ── */}
      {isImportModalOpen && (
        <div
          onClick={closeImportModal}
          style={{ position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", background: "#fff", borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.5)", padding: "28px 32px 24px", width: "min(1200px, 95vw)", maxHeight: "83vh", display: "flex", flexDirection: "column" }}
          >
            <button
              type="button"
              onClick={closeImportModal}
              style={{ position: "absolute", top: -10, right: -58, background: "transparent", border: "none", color: "#fff", width: 36, height: 36, fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
            >✕</button>

            {/* Modal header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "#2F2E40", letterSpacing: 0.3 }}>
                IMPORT FROM BATCH
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#2F2E40", cursor: "pointer", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    className="delete-question-checkbox-dqc"
                    checked={importContentWithFolder}
                    onChange={(e) => setImportContentWithFolder(e.target.checked)}
                    style={{ flexShrink: 0 }}
                  />
                  Content with folder structure
                </label>
                <button
                  type="button"
                  disabled={!hasImportSelection}
                  onClick={handleImportConfirm}
                  style={{
                    padding: "6px 20px",
                    background: !hasImportSelection ? "#ccc" : "#2F2E40",
                    color: "#fff",
                    border: "none",
                    borderRadius: 5,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: !hasImportSelection ? "not-allowed" : "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  IMPORT {hasImportSelection ? `(${importCheckedContentIds.size})` : ""}
                </button>
              </div>
            </div>

            {/* Where the import will go */}
            <div style={{ fontSize: 12, color: "#777", marginBottom: 14 }}>
              {importTargetPath
                ? <>Content of the checked folders will be added into: <b>{importTargetPath.join("\\")}</b></>
                : "Checked folders will be added to the Content Folder list as folders."}
            </div>

            <div style={{ marginBottom: 16, maxWidth: 320 }}>
              <DropDown
                label="Batch Name"
                selected={importSelectedBatch ? importSelectedBatch.batch_id : null}
                setSelected={(id) => setImportSelectedBatch(batches.find((b) => b.batch_id === id))}
                options={batches
                  .filter((b) => !selectedBatch || b.batch_id !== selectedBatch.batch_id)
                  .map((b) => ({ id: b.batch_id, label: b.batch_name }))}
                isRequired={true}
                Height="auto"
                minheight="200px"
                OverFlow="auto"
              />
            </div>

            {isImportFoldersLoading && (
              <div style={{ fontSize: 13, color: "#888", padding: "10px 0" }}>Loading folders...</div>
            )}
            {!isImportFoldersLoading && importFoldersError && (
              <div style={{ fontSize: 13, color: "#e53935", padding: "10px 0" }}>⚠ {importFoldersError}</div>
            )}
            {!isImportFoldersLoading && !importFoldersError && importFolders.length > 0 && (
              <div style={{ overflowY: "auto", flex: 1 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <td style={{ fontSize: 12, fontWeight: 600, color: "#555", paddingBottom: 8, paddingLeft: 12, borderBottom: "1px solid #e0e0e0", width: 280 }}>Chapter/Topic</td>
                      <td style={{ fontSize: 12, fontWeight: 600, color: "#555", paddingBottom: 8, paddingLeft: 285, borderBottom: "1px solid #e0e0e0", width: 260 }}>PDF</td>
                      <td style={{ fontSize: 12, fontWeight: 600, color: "#555", paddingBottom: 8, paddingLeft: 191, borderBottom: "1px solid #e0e0e0" }}>YouTube</td>
                    </tr>
                  </thead>
                  <tbody>
                    {importFolders
                      .filter((f) => hasAnyContent(f, importFolderPdfs, importFolderYoutube))
                      .map((f) => renderImportFolderRow(f, 0))}
                  </tbody>
                </table>
              </div>
            )}
            {!isImportFoldersLoading && !importFoldersError && importFolders.length === 0 && importSelectedBatch && (
              <div style={{ fontSize: 13, color: "#bbb", padding: "10px 0" }}>No content found for this batch.</div>
            )}
            {!importSelectedBatch && (
              <div style={{ fontSize: 13, color: "#bbb", padding: "10px 0" }}>Select a batch to preview its content.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PdfViewer;




















// import React, { useState, useEffect, useRef, useCallback } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import DropDown from "../../Components/Shared/Dropdown/Dropdown";
// import { fetchCourseData } from "./../../Redux/StudentRegistration/courseSlice";
// import { FULL_API_BASE_URL } from "../../Config/Config";
// import "./contentresources.css";

// // ── Load YT IFrame API once globally ──────────────────────────────────────────
// let ytApiLoaded = false;
// let ytApiReady = false;
// const ytReadyCallbacks = [];

// function loadYTApi() {
//   if (ytApiLoaded) return;
//   ytApiLoaded = true;
//   const tag = document.createElement("script");
//   tag.src = "https://www.youtube.com/iframe_api";
//   document.head.appendChild(tag);
//   window.onYouTubeIframeAPIReady = () => {
//     ytApiReady = true;
//     ytReadyCallbacks.forEach((cb) => cb());
//     ytReadyCallbacks.length = 0;
//   };
// }

// function onYTReady(cb) {
//   if (ytApiReady) { cb(); return; }
//   ytReadyCallbacks.push(cb);
//   loadYTApi();
// }

// function getYoutubeVideoId(url) {
//   try {
//     const u = new URL(url.trim());
//     if (u.hostname === "youtu.be") return u.pathname.replace("/", "");
//     if (u.hostname.includes("youtube.com")) {
//       if (u.pathname === "/watch") return u.searchParams.get("v");
//       if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1];
//       if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/shorts/")[1];
//     }
//   } catch { /* ignore */ }
//   return null;
// }

// function formatTime(sec) {
//   if (!sec || isNaN(sec)) return "0:00";
//   const m = Math.floor(sec / 60);
//   const s = Math.floor(sec % 60);
//   return `${m}:${s < 10 ? "0" : ""}${s}`;
// }

// function mapApiFolderToTree(node, parentPath = "") {
//   const uniqueId = parentPath ? `${parentPath}-${node.folder_id}` : `f${node.folder_id}`;
//   return {
//     id: uniqueId,
//     name: node.folder_name,
//     is_real: node.is_real === true,
//     isPendingNew: false,
//     folderId: node.folder_id,
//     studyContentId: node.study_content_id,
//     docs: node.docs || [],
//     videos: node.videos || [],
//     children: (node.children || []).map((c) => mapApiFolderToTree(c, uniqueId)),
//   };
// }

// // ── Get full path of a folder by id ──────────────────────────────────────
// const getFolderPath = (folders, targetId, path = []) => {
//   for (const f of folders) {
//     const current = [...path, f.name];
//     if (f.id === targetId) return current;
//     if (f.children?.length) {
//       const found = getFolderPath(f.children, targetId, current);
//       if (found) return found;
//     }
//   }
//   return null;
// };

// function displayNameFromKey(key) {
//   if (!key) return "file.pdf";
//   const last = key.split("/").pop();
//   const underscoreIdx = last.indexOf("_");
//   return underscoreIdx >= 0 ? last.slice(underscoreIdx + 1) : last;
// }

// function pinnedFieldsFromPath(path) {
//   const chapter_id = path[0] && path[0].is_real ? path[0].id : null;
//   const topic_id = chapter_id !== null && path[1] && path[1].is_real ? path[1].id : null;
//   return { chapter_id, topic_id };
// }

// function seedContentState(nodes, pdfsMap, ytMap, pdfBaseline, ytBaseline) {
//   nodes.forEach((n) => {
//     const pdfEntries = (n.docs || []).map((d) => ({
//       fileName: displayNameFromKey(d.pdf_file_name),
//       previewUrl: d.pdf_url,
//       pdfFileName: d.pdf_file_name,
//     }));
//     pdfsMap[n.id] = pdfEntries;
//     pdfBaseline[n.id] = pdfEntries.map((p) => p.pdfFileName);

//     const ytLinks = (n.videos || []).map((v) => v.yt_video_url);
//     ytMap[n.id] = { list: ytLinks, url: "", error: "" };
//     ytBaseline[n.id] = ytLinks;

//     if (n.children?.length) seedContentState(n.children, pdfsMap, ytMap, pdfBaseline, ytBaseline);
//   });
// }

// function collectStudyContentIds(nodes, out) {
//   nodes.forEach((n) => {
//     if (n.studyContentId) out.add(n.studyContentId);
//     if (n.children?.length) collectStudyContentIds(n.children, out);
//   });
// }

// function collectDeletedRoots(originalNodes, currentIdSet, out) {
//   originalNodes.forEach((n) => {
//     if (n.studyContentId && !currentIdSet.has(n.studyContentId)) {
//       out.push(n.studyContentId);
//     } else if (n.children?.length) {
//       collectDeletedRoots(n.children, currentIdSet, out);
//     }
//   });
// }

// // ── Diff two lists WITHOUT collapsing duplicates ──────────────────────────
// // added   = items in `current` beyond what `baseline` already had
// // removed = items in `baseline` that are no longer in `current`
// function diffList(current, baseline) {
//   const remaining = [...baseline];
//   const added = [];
//   current.forEach((item) => {
//     const i = remaining.indexOf(item);
//     if (i >= 0) remaining.splice(i, 1);
//     else added.push(item);
//   });
//   return { added, removed: remaining };
// }

// // ── hasAnyContent helper for import modal filtering ───────────────────────
// function hasAnyContent(node, pdfsMap, ytMap) {
//   const pdfs = pdfsMap[node.id] || [];
//   const yts = (ytMap[node.id] || {}).list || [];
//   if (pdfs.length > 0 || yts.length > 0) return true;
//   return (node.children || []).some((c) => hasAnyContent(c, pdfsMap, ytMap));
// }

// async function saveStudyContentBatch({ uploadedBy, item, pdfFiles, accessToken }) {
//   const formData = new FormData();
//   formData.append("uploaded_by", uploadedBy);
//   formData.append("items", JSON.stringify([item]));

//   (pdfFiles || []).forEach((file) => {
//     const renamed = new File([file], `0__${file.name}`, { type: file.type });
//     formData.append("pdf_files", renamed);
//   });

//   const res = await fetch(`${FULL_API_BASE_URL}/save-study-content/`, {
//     method: "POST",
//     headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
//     body: formData,
//   });
//   const json = await res.json();
//   if (!res.ok || json?.status !== 200) {
//     throw new Error(json?.message || `Request failed with status ${res.status}`);
//   }
//   return json.data[0];
// }

// function PdfViewer({ dataClient, setMessageBox, setIsMessageBoxVisible, setActiveImg }) {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const userName = dataClient?.client_schema_name?.[6]?.[0]?.user_name;
//   const { coursesdata } = useSelector((state) => state.course);

//   const [isOpenSubject, setIsOpenSubject] = useState(true);

//   const [pdfFile, setPdfFile] = useState(null);
//   const [pdfFileName, setPdfFileName] = useState("");
//   const [pdfError, setPdfError] = useState("");
//   const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

//   const [value, setValues] = useState({ course_id: "" });
//   const [subjects, setSubjects] = useState([]);
//   const [selectedSubject, setSelectedSubject] = useState(null);
//   const [selectedSubjectLabel, setSelectedSubjectLabel] = useState("");

//   const [batches, setBatches] = useState([]);
//   const [selectedBatch, setSelectedBatch] = useState(null);

//   // PDF modal
//   const [modalPdfUrl, setModalPdfUrl] = useState(null);
//   const [modalPdfName, setModalPdfName] = useState("");
//   const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

//   // ── YouTube player modal state ─────────────────────────────────────────────
//   const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
//   const [ytVideoId, setYtVideoId] = useState(null);

//   const ytDivRef = useRef(null);
//   const playerRef = useRef(null);
//   const timeUpdaterRef = useRef(null);
//   const idleTimerRef = useRef(null);
//   const containerRef = useRef(null);

//   const [isPlaying, setIsPlaying] = useState(false);
//   const [currentTimeFmt, setCurrentTimeFmt] = useState("0:00");
//   const [totalTimeFmt, setTotalTimeFmt] = useState("0:00");
//   const [progressVal, setProgressVal] = useState(0);
//   const [durationVal, setDurationVal] = useState(0);
//   const [speed, setSpeed] = useState(1);
//   const [uiHidden, setUiHidden] = useState(false);
//   const [isKioskFS, setIsKioskFS] = useState(false);

//   const [isOpenCustomFolder, setIsOpenCustomFolder] = useState(true);
//   const [allFolders, setAllFolders] = useState([]);
//   const [newFolderName, setNewFolderName] = useState("");
//   const [expandedFolders, setExpandedFolders] = useState({});
//   const [folderPdfs, setFolderPdfs] = useState({});
//   const [folderYoutube, setFolderYoutube] = useState({});
//   const ytTitleCacheRef = useRef({});
//   const [selectedFolderId, setSelectedFolderId] = useState(null);

//   const [isFoldersLoading, setIsFoldersLoading] = useState(false);
//   const [foldersError, setFoldersError] = useState("");

//   // ── Dirty / unsaved-changes tracker ──────────────────────────────────────
//   const [isContentDirty, setIsContentDirty] = useState(false);
//   const [isSaving, setIsSaving] = useState(false);

//   // ── Pending batch-detail change ───────────────────────────────────────────
//   const pendingBatchChange = useRef(null);

//   // ── Save-time diffing state ───────────────────────────────────────────────
//   const originalFoldersSnapshotRef = useRef([]);
//   const folderPdfsBaselineRef = useRef({});
//   const folderYoutubeBaselineRef = useRef({});

//   // ── Import modal state ────────────────────────────────────────────────────
//   const [isImportModalOpen, setIsImportModalOpen] = useState(false);
//   const [importSelectedBatch, setImportSelectedBatch] = useState(null);
//   // null  => no folder selected on main screen -> imported folders are ADDED as folders
//   // value => folder selected on main screen    -> imported content is put INTO that folder
//   const [importTargetFolderId, setImportTargetFolderId] = useState(null);
//   const [importCheckedIds, setImportCheckedIds] = useState(new Set());

//   const [importFolders, setImportFolders] = useState([]);
//   const [importFolderPdfs, setImportFolderPdfs] = useState({});
//   const [importFolderYoutube, setImportFolderYoutube] = useState({});
//   const [isImportFoldersLoading, setIsImportFoldersLoading] = useState(false);
//   const [importFoldersError, setImportFoldersError] = useState("");
//   const [importExpandedFolders, setImportExpandedFolders] = useState({});

//   const [importContentWithFolder, setImportContentWithFolder] = useState(true);
//   // ── Helper: show the YES/NO confirmation modal ────────────────────────────
//   const showUnsavedWarning = (onConfirm) => {
//     setMessageBox(
//       <div className="Container-messageBOX">
//         <div className="messagebox-insider1">
//           <div className="loader-tc"></div>
//           <span>
//             You have unsaved changes in the Content Folder. Changing batch
//             details will discard them. Do you want to proceed?
//           </span>
//         </div>
//         <div
//           className="messagebox-insider2"
//           style={{ display: "flex", gap: "10px", marginTop: "10px" }}
//         >
//           <button
//             className="messagebox-cancel-btn-ok"
//             onClick={() => {
//               setIsMessageBoxVisible(false);
//               onConfirm();
//             }}
//           >
//             YES
//           </button>
//           <button
//             className="messagebox-cancel-btn-ok"
//             onClick={() => setIsMessageBoxVisible(false)}
//           >
//             NO
//           </button>
//         </div>
//       </div>
//     );
//     setIsMessageBoxVisible(true);
//   };

//   // ── Guarded batch-detail setters ──────────────────────────────────────────
//   const handleCourseChange = (sel) => {
//     const doChange = () => {
//       setValues({ course_id: sel });
//       setIsContentDirty(false);
//     };
//     if (isContentDirty) showUnsavedWarning(doChange);
//     else doChange();
//   };

//   const handleSubjectChange = (sel) => {
//     const doChange = () => {
//       setSelectedSubject(sel);
//       setIsContentDirty(false);
//     };
//     if (isContentDirty) showUnsavedWarning(doChange);
//     else doChange();
//   };

//   const handleBatchSelection = (id) => {
//     const doChange = () => {
//       setSelectedBatch(batches.find((b) => b.batch_id === id));
//       setIsContentDirty(false);
//     };
//     if (isContentDirty) showUnsavedWarning(doChange);
//     else doChange();
//   };

//   const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

//   // ── Add folder/sub-folder ─────────────────────────────────────────────────
//   // const handleAddFolder = () => {
//   //   const input = newFolderName.trim();
//   //   if (!input) return;

//   //   const parts = input.split("\\").map((p) => p.trim()).filter(Boolean);

//   //   const buildChain = (names) => {
//   //     let node = { id: Date.now() + names.length, name: names[names.length - 1], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
//   //     for (let i = names.length - 2; i >= 0; i--) {
//   //       node = { id: Date.now() + i, name: names[i], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [node] };
//   //     }
//   //     return node;
//   //   };

//   //   const newNode = parts.length === 1
//   //     ? { id: Date.now(), name: parts[0], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] }
//   //     : buildChain(parts);

//   //   if (!selectedFolderId) {
//   //     setAllFolders((prev) => [...prev, newNode]);
//   //   } else {
//   //     const addChildRecursive = (folders) =>
//   //       folders.map((f) => {
//   //         if (f.id === selectedFolderId)
//   //           return { ...f, children: [...(f.children || []), newNode] };
//   //         if (f.children?.length)
//   //           return { ...f, children: addChildRecursive(f.children) };
//   //         return f;
//   //       });
//   //     setAllFolders((prev) => addChildRecursive(prev));
//   //     setExpandedFolders((prev) => ({ ...prev, [selectedFolderId]: true }));
//   //   }

//   //   if (parts.length > 1) {
//   //     const collectIds = (node) => {
//   //       const ids = [node.id];
//   //       node.children?.forEach((c) => ids.push(...collectIds(c)));
//   //       return ids;
//   //     };
//   //     const allIds = collectIds(newNode);
//   //     allIds.slice(0, -1).forEach((id) =>
//   //       setExpandedFolders((prev) => ({ ...prev, [id]: true }))
//   //     );
//   //   }

//   //   setNewFolderName("");
//   //   setSelectedFolderId(null);
//   //   setIsContentDirty(true);
//   // };

//   const getFolderDepth = (folders, targetId, depth = 1) => {
//     for (const f of folders) {
//       if (f.id === targetId) return depth;
//       if (f.children?.length) {
//         const found = getFolderDepth(f.children, targetId, depth + 1);
//         if (found) return found;
//       }
//     }
//     return null;
//   };

//   const handleAddFolder = () => {
//     const input = newFolderName.trim();
//     if (!input) return;

//     const parts = input.split("\\").map((p) => p.trim()).filter(Boolean);

//     // ── Depth guard ───────────────────────────────────────────────────────
//     const selectedDepth = selectedFolderId
//       ? (getFolderDepth(allFolders, selectedFolderId) ?? 0)
//       : 0;
//     const totalDepth = selectedDepth + parts.length;

//     if (totalDepth > 4) {
//       setIsMessageBoxVisible(true);
//       setMessageBox(
//         <div className="Container-messageBOX">
//           <div className="messagebox-insider1">
//             <div className="loader-tc"></div>
//             <span style={{ marginLeft: 10, marginTop: 3 }}>
//               Cannot add folder: maximum folder depth is 4 levels.
//             </span>
//           </div>
//         </div>
//       );
//       setActiveImg(false);
//       return;
//     }

//     const buildChain = (names) => {
//       let node = { id: Date.now() + names.length, name: names[names.length - 1], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
//       for (let i = names.length - 2; i >= 0; i--) {
//         node = { id: Date.now() + i, name: names[i], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [node] };
//       }
//       return node;
//     };

//     const newNode = parts.length === 1
//       ? { id: Date.now(), name: parts[0], is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] }
//       : buildChain(parts);

//     if (!selectedFolderId) {
//       setAllFolders((prev) => [...prev, newNode]);
//     } else {
//       const addChildRecursive = (folders) =>
//         folders.map((f) => {
//           if (f.id === selectedFolderId)
//             return { ...f, children: [...(f.children || []), newNode] };
//           if (f.children?.length)
//             return { ...f, children: addChildRecursive(f.children) };
//           return f;
//         });
//       setAllFolders((prev) => addChildRecursive(prev));
//       setExpandedFolders((prev) => ({ ...prev, [selectedFolderId]: true }));
//     }

//     if (parts.length > 1) {
//       const collectIds = (node) => {
//         const ids = [node.id];
//         node.children?.forEach((c) => ids.push(...collectIds(c)));
//         return ids;
//       };
//       const allIds = collectIds(newNode);
//       allIds.slice(0, -1).forEach((id) =>
//         setExpandedFolders((prev) => ({ ...prev, [id]: true }))
//       );
//     }

//     setNewFolderName("");
//     setSelectedFolderId(null);
//     setIsContentDirty(true);
//   };

//   // ── Delete folder ─────────────────────────────────────────────────────────
//   const handleDeleteFolder = (id) => {
//     const removeRecursive = (folders) =>
//       folders
//         .filter((f) => f.id !== id)
//         .map((f) => ({ ...f, children: f.children ? removeRecursive(f.children) : [] }));
//     setAllFolders((prev) => removeRecursive(prev));
//     setFolderPdfs((prev) => { const n = { ...prev }; delete n[id]; return n; });
//     setFolderYoutube((prev) => { const n = { ...prev }; delete n[id]; return n; });
//     if (selectedFolderId === id) setSelectedFolderId(null);
//     setIsContentDirty(true);
//   };

//   const handleSelectFolder = (id) => {
//     setSelectedFolderId((prev) => (prev === id ? null : id));
//   };

//   const handleFolderPdfUpload = (id, files) => {
//     const newPdfs = Array.from(files)
//       .filter((f) => f.name.endsWith(".pdf"))
//       .map((f) => ({ file: f, fileName: f.name, previewUrl: URL.createObjectURL(f) }));
//     setFolderPdfs((prev) => ({ ...prev, [id]: [...(prev[id] || []), ...newPdfs] }));
//     setIsContentDirty(true);
//   };

//   const clearFolderPdf = (id, idx) => {
//     URL.revokeObjectURL(folderPdfs[id][idx].previewUrl);
//     setFolderPdfs((prev) => ({ ...prev, [id]: prev[id].filter((_, i) => i !== idx) }));
//     setIsContentDirty(true);
//   };

//   const toggleExpandFolder = (id) =>
//     setExpandedFolders((p) => ({ ...p, [id]: !p[id] }));

//   // ── Import checkbox handler (only tracks the selection) ───────────────────
//   // const handleImportCheckbox = (folder, checked) => {
//   //   setImportCheckedIds((prev) => {
//   //     const next = new Set(prev);
//   //     if (checked) next.add(folder.id);
//   //     else next.delete(folder.id);
//   //     return next;
//   //   });
//   // };

//   // CHECKBOX TO SELECTE AND UNSELECT WITH CHILD AND PARENT
//   const handleImportCheckbox = (folder, checked) => {
//     // collect all descendant ids of a node
//     const collectAllIds = (node) => {
//       const ids = [node.id];
//       (node.children || []).forEach((c) => ids.push(...collectAllIds(c)));
//       return ids;
//     };

//     setImportCheckedIds((prev) => {
//       const next = new Set(prev);
//       const allIds = collectAllIds(folder);
//       if (checked) {
//         allIds.forEach((id) => next.add(id));
//       } else {
//         allIds.forEach((id) => next.delete(id));
//       }
//       return next;
//     });

//     // also expand the folder so children are visible
//     if (checked && (folder.children || []).length > 0) {
//       const collectExpandIds = (node) => {
//         const ids = [node.id];
//         (node.children || []).forEach((c) => ids.push(...collectExpandIds(c)));
//         return ids;
//       };
//       const expandIds = collectExpandIds(folder);
//       setImportExpandedFolders((prev) => {
//         const next = { ...prev };
//         expandIds.forEach((id) => { next[id] = true; });
//         return next;
//       });
//     }
//   };

//   // ── Close import modal helper ─────────────────────────────────────────────
//   // const closeImportModal = () => {
//   //   setIsImportModalOpen(false);
//   //   setImportSelectedBatch(null);
//   //   setImportFolders([]);
//   //   setImportCheckedIds(new Set());
//   //   setImportTargetFolderId(null);
//   // };

//   const closeImportModal = () => {
//     setIsImportModalOpen(false);
//     setImportSelectedBatch(null);
//     setImportFolders([]);
//     setImportCheckedIds(new Set());
//     setImportTargetFolderId(null);
//     setImportContentWithFolder(true);
//   };

//   // ── Open import modal (no folder needs to be selected) ────────────────────
//   const openImportModal = () => {
//     if (!(value.course_id && selectedSubject && selectedBatch)) {
//       setIsMessageBoxVisible(true);
//       setMessageBox(
//         <div className="Container-messageBOX">
//           <div className="messagebox-insider1">
//             <div className="loader-tc"></div>
//             <span style={{ marginLeft: 10, marginTop: 3 }}>
//               Select Course, Subject and Batch first.
//             </span>
//           </div>
//         </div>
//       );
//       setActiveImg(false);
//       return;
//     }
//     // selectedFolderId may be null -> that is fine
//     setImportTargetFolderId(selectedFolderId);
//     setImportCheckedIds(new Set());
//     setIsImportModalOpen(true);
//   };

//   // ── Confirm import ────────────────────────────────────────────────────────
//   // MODE A (a folder is selected on the main screen):
//   //    content (PDFs / YouTube) of every checked folder is put INTO that folder.
//   // MODE B (no folder selected):
//   //    every checked folder is ADDED to the main screen, based on is_real:
//   //      is_real === true  -> the same chapter/topic already exists in the main list
//   //                           (same id), so its content is merged there. If it is
//   //                           missing it is created as a real folder with that id.
//   //      is_real === false -> a NEW custom folder is created (or an existing custom
//   //                           folder with the same name at that level is reused).
//   //    Parent folders of a checked folder are created/found automatically so the
//   //    hierarchy is kept.
//   // const handleImportConfirm = () => {
//   //   if (importCheckedIds.size === 0) return;

//   //   const pdfAdds = {};
//   //   const ytAdds = {};
//   //   const expandAdds = {};
//   //   let nextTree = null;

//   //   const collectContent = (impNode, mainId) => {
//   //     const pdfs = (importFolderPdfs[impNode.id] || []).map((p) => ({
//   //       fileName: p.fileName,
//   //       previewUrl: p.previewUrl,
//   //       pdfFileName: p.pdfFileName,
//   //     }));
//   //     const yts = (importFolderYoutube[impNode.id] || {}).list || [];
//   //     if (pdfs.length) pdfAdds[mainId] = [...(pdfAdds[mainId] || []), ...pdfs];
//   //     if (yts.length) ytAdds[mainId] = [...(ytAdds[mainId] || []), ...yts];
//   //   };

//   //  if (importTargetFolderId) {
//   //   // ── MODE A ── add checked folder as sub-folder inside target
//   //   const tree = deepClone(allFolders);
//   //   let seq = 0;
//   //   const makeId = () => `imp-${Date.now()}-${seq++}`;

//   //   const findNode = (nodes, id) => {
//   //     for (const n of nodes) {
//   //       if (n.id === id) return n;
//   //       const found = findNode(n.children || [], id);
//   //       if (found) return found;
//   //     }
//   //     return null;
//   //   };

//   //   const targetNode = findNode(tree, importTargetFolderId);

//   //   if (targetNode) {
//   //     if (!targetNode.children) targetNode.children = [];

//   //     // const walkImport = (impNode, siblings) => {
//   //     //   if (!importCheckedIds.has(impNode.id)) {
//   //     //     // not checked — recurse children anyway
//   //     //     (impNode.children || []).forEach((c) => walkImport(c, siblings));
//   //     //     return;
//   //     //   }

//   //     //   // find or create matching sub-folder inside siblings
//   //     //   // match by folderId (is_real) or by name (custom)
//   //     //   let mainNode;
//   //     //   if (impNode.is_real) {
//   //     //     // match by folderId since ids differ between batches
//   //     //     mainNode = siblings.find(
//   //     //       (s) => s.is_real && s.folderId === impNode.folderId
//   //     //     );
//   //     //     if (!mainNode) {
//   //     //       mainNode = {
//   //     //         id: makeId(),
//   //     //         name: impNode.name,
//   //     //         is_real: true,
//   //     //         isPendingNew: false,
//   //     //         folderId: impNode.folderId,
//   //     //         studyContentId: null,
//   //     //         docs: [],
//   //     //         videos: [],
//   //     //         children: [],
//   //     //       };
//   //     //       siblings.push(mainNode);
//   //     //     }
//   //     //   } else {
//   //     //     const nm = (impNode.name || "").trim().toLowerCase();
//   //     //     mainNode = siblings.find(
//   //     //       (s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm
//   //     //     );
//   //     //     if (!mainNode) {
//   //     //       mainNode = {
//   //     //         id: makeId(),
//   //     //         name: impNode.name,
//   //     //         is_real: false,
//   //     //         isPendingNew: true,
//   //     //         folderId: null,
//   //     //         studyContentId: null,
//   //     //         docs: [],
//   //     //         videos: [],
//   //     //         children: [],
//   //     //       };
//   //     //       siblings.push(mainNode);
//   //     //     }
//   //     //   }

//   //     //   if (!mainNode.children) mainNode.children = [];

//   //     //   // always collect content into this sub-folder
//   //     //   collectContent(impNode, mainNode.id);
//   //     //   expandAdds[importTargetFolderId] = true;
//   //     //   expandAdds[mainNode.id] = true;

//   //     //   // recurse children of checked node into sub-folder's children
//   //     //   (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
//   //     // };

//   //     const walkImport = (impNode, siblings) => {
//   //       if (!importCheckedIds.has(impNode.id)) {
//   //         (impNode.children || []).forEach((c) => walkImport(c, siblings));
//   //         return;
//   //       }

//   //       if (impNode.is_real) {
//   //         // check if this real folder already exists in siblings
//   //         const mainNode = siblings.find(
//   //           (s) => s.is_real && s.folderId === impNode.folderId
//   //         );

//   //         if (mainNode) {
//   //           // exists → merge content into it
//   //           collectContent(impNode, mainNode.id);
//   //           expandAdds[importTargetFolderId] = true;
//   //           expandAdds[mainNode.id] = true;
//   //           if (!mainNode.children) mainNode.children = [];
//   //           (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
//   //         } else {
//   //           // does NOT exist as sibling → dump content directly into target folder
//   //           collectContent(impNode, importTargetFolderId);
//   //           expandAdds[importTargetFolderId] = true;
//   //           // still recurse children into siblings (target's children)
//   //           (impNode.children || []).forEach((c) => walkImport(c, siblings));
//   //         }
//   //         return;
//   //       }

//   //       // custom folder (is_real: false)
//   //       const nm = (impNode.name || "").trim().toLowerCase();
//   //       let mainNode = siblings.find(
//   //         (s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm
//   //       );
//   //       if (!mainNode) {
//   //         mainNode = {
//   //           id: makeId(),
//   //           name: impNode.name,
//   //           is_real: false,
//   //           isPendingNew: true,
//   //           folderId: null,
//   //           studyContentId: null,
//   //           docs: [],
//   //           videos: [],
//   //           children: [],
//   //         };
//   //         siblings.push(mainNode);
//   //       }

//   //       if (!mainNode.children) mainNode.children = [];
//   //       collectContent(impNode, mainNode.id);
//   //       expandAdds[importTargetFolderId] = true;
//   //       expandAdds[mainNode.id] = true;
//   //       (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
//   //     };
//   //     importFolders.forEach((f) => walkImport(f, targetNode.children));
//   //   }

//   //   nextTree = tree;
//   // } else {
//   //     // ── MODE B ── same as before
//   //     const tree = deepClone(allFolders);
//   //     let seq = 0;
//   //     const makeId = () => `imp-${Date.now()}-${seq++}`;

//   //     const hasChecked = (n) =>
//   //       importCheckedIds.has(n.id) || (n.children || []).some(hasChecked);

//   //     const processNode = (impNode, siblings) => {
//   //       if (!hasChecked(impNode)) return false;

//   //       let mainNode;
//   //     if (impNode.is_real) {
//   //   // match by folderId, not by tree-generated id
//   //   mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
//   //   if (!mainNode) {
//   //     mainNode = {
//   //       id: makeId(),
//   //       name: impNode.name,
//   //       is_real: true,
//   //       isPendingNew: false,
//   //       folderId: impNode.folderId,
//   //       studyContentId: null,
//   //       docs: [],
//   //       videos: [],
//   //       children: [],
//   //     };
//   //     siblings.push(mainNode);
//   //   }
//   // } else {
//   //         const nm = (impNode.name || "").trim().toLowerCase();
//   //         mainNode = siblings.find(
//   //           (s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm
//   //         );
//   //         if (!mainNode) {
//   //           mainNode = {
//   //             id: makeId(),
//   //             name: impNode.name,
//   //             is_real: false,
//   //             isPendingNew: true,
//   //             folderId: null,
//   //             studyContentId: null,
//   //             docs: [],
//   //             videos: [],
//   //             children: [],
//   //           };
//   //           siblings.push(mainNode);
//   //         }
//   //       }

//   //       if (!mainNode.children) mainNode.children = [];
//   //       if (importCheckedIds.has(impNode.id)) collectContent(impNode, mainNode.id);

//   //       let childAdded = false;
//   //       (impNode.children || []).forEach((c) => {
//   //         if (processNode(c, mainNode.children)) childAdded = true;
//   //       });
//   //       if (childAdded) expandAdds[mainNode.id] = true;
//   //       return true;
//   //     };

//   //     importFolders.forEach((f) => processNode(f, tree));
//   //     nextTree = tree;
//   //   }

//   //   if (nextTree) setAllFolders(nextTree);

//   //   setFolderPdfs((prev) => {
//   //     const next = { ...prev };
//   //     Object.entries(pdfAdds).forEach(([id, entries]) => {
//   //       next[id] = [...(next[id] || []), ...entries];
//   //     });
//   //     return next;
//   //   });

//   //   setFolderYoutube((prev) => {
//   //     const next = { ...prev };
//   //     Object.entries(ytAdds).forEach(([id, links]) => {
//   //       const existing = next[id] || { list: [], url: "", error: "" };
//   //       next[id] = { ...existing, list: [...(existing.list || []), ...links] };
//   //     });
//   //     return next;
//   //   });

//   //   setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
//   //   setIsContentDirty(true);
//   //   closeImportModal();
//   // };

//   const handleImportConfirm = () => {
//     if (importCheckedIds.size === 0) return;

//     const pdfAdds = {};
//     const ytAdds = {};
//     const expandAdds = {};
//     let nextTree = null;

//     const withContent = importContentWithFolder;
//     const curFolderPdfs = importFolderPdfs;
//     const curFolderYoutube = importFolderYoutube;
//     const curCheckedIds = importCheckedIds;
//     const curTargetId = importTargetFolderId;

//     // ── UNCHECKED + folder selected → sirf content dump into target ──────────
//     if (!withContent && curTargetId) {
//       const walkForContent = (impNode) => {
//         if (curCheckedIds.has(impNode.id)) {
//           const pdfs = (curFolderPdfs[impNode.id] || []).map((p) => ({
//             fileName: p.fileName,
//             previewUrl: p.previewUrl,
//             pdfFileName: p.pdfFileName,
//           }));
//           const yts = (curFolderYoutube[impNode.id] || {}).list || [];
//           if (pdfs.length) pdfAdds[curTargetId] = [...(pdfAdds[curTargetId] || []), ...pdfs];
//           if (yts.length) ytAdds[curTargetId] = [...(ytAdds[curTargetId] || []), ...yts];
//           expandAdds[curTargetId] = true;
//         }
//         (impNode.children || []).forEach(walkForContent);
//       };
//       importFolders.forEach(walkForContent);

//       setFolderPdfs((prev) => {
//         const next = { ...prev };
//         Object.entries(pdfAdds).forEach(([id, entries]) => {
//           next[id] = [...(next[id] || []), ...entries];
//         });
//         return next;
//       });
//       setFolderYoutube((prev) => {
//         const next = { ...prev };
//         Object.entries(ytAdds).forEach(([id, links]) => {
//           const existing = next[id] || { list: [], url: "", error: "" };
//           next[id] = { ...existing, list: [...(existing.list || []), ...links] };
//         });
//         return next;
//       });
//       setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
//       setIsContentDirty(true);
//       closeImportModal();
//       return;
//     }

//     // ── UNCHECKED + no folder selected → sirf folder structure, no content ───
//     if (!withContent && !curTargetId) {
//       const tree = deepClone(allFolders);
//       let seq = 0;
//       const makeId = () => `imp-${Date.now()}-${seq++}`;

//       const hasChecked = (n) =>
//         curCheckedIds.has(n.id) || (n.children || []).some(hasChecked);

//       const processNode = (impNode, siblings) => {
//         if (!hasChecked(impNode)) return false;
//         let mainNode;
//         if (impNode.is_real) {
//           mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
//           if (!mainNode) {
//             mainNode = { id: makeId(), name: impNode.name, is_real: true, isPendingNew: false, folderId: impNode.folderId, studyContentId: null, docs: [], videos: [], children: [] };
//             siblings.push(mainNode);
//           }
//         } else {
//           const nm = (impNode.name || "").trim().toLowerCase();
//           mainNode = siblings.find((s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm);
//           if (!mainNode) {
//             mainNode = { id: makeId(), name: impNode.name, is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
//             siblings.push(mainNode);
//           }
//         }
//         if (!mainNode.children) mainNode.children = [];
//         // content intentionally skipped
//         let childAdded = false;
//         (impNode.children || []).forEach((c) => { if (processNode(c, mainNode.children)) childAdded = true; });
//         if (childAdded) expandAdds[mainNode.id] = true;
//         return true;
//       };

//       importFolders.forEach((f) => processNode(f, tree));
//       setAllFolders(tree);
//       setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
//       setIsContentDirty(true);
//       closeImportModal();
//       return;
//     }

//     // ── CHECKED: folder + content import ─────────────────────────────────────
//     const collectContent = (impNode, mainId) => {
//       const pdfs = (curFolderPdfs[impNode.id] || []).map((p) => ({
//         fileName: p.fileName,
//         previewUrl: p.previewUrl,
//         pdfFileName: p.pdfFileName,
//       }));
//       const yts = (curFolderYoutube[impNode.id] || {}).list || [];
//       if (pdfs.length) pdfAdds[mainId] = [...(pdfAdds[mainId] || []), ...pdfs];
//       if (yts.length) ytAdds[mainId] = [...(ytAdds[mainId] || []), ...yts];
//     };

//     if (curTargetId) {
//       // ── MODE A ── checked + folder selected
//       const tree = deepClone(allFolders);
//       let seq = 0;
//       const makeId = () => `imp-${Date.now()}-${seq++}`;

//       const findNode = (nodes, id) => {
//         for (const n of nodes) {
//           if (n.id === id) return n;
//           const found = findNode(n.children || [], id);
//           if (found) return found;
//         }
//         return null;
//       };

//       const targetNode = findNode(tree, curTargetId);
//       if (targetNode) {
//         if (!targetNode.children) targetNode.children = [];

//         const walkImport = (impNode, siblings) => {
//           if (!curCheckedIds.has(impNode.id)) {
//             (impNode.children || []).forEach((c) => walkImport(c, siblings));
//             return;
//           }
//           if (impNode.is_real) {
//             const mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
//             if (mainNode) {
//               collectContent(impNode, mainNode.id);
//               expandAdds[curTargetId] = true;
//               expandAdds[mainNode.id] = true;
//               if (!mainNode.children) mainNode.children = [];
//               (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
//             } else {
//               collectContent(impNode, curTargetId);
//               expandAdds[curTargetId] = true;
//               (impNode.children || []).forEach((c) => walkImport(c, siblings));
//             }
//             return;
//           }
//           const nm = (impNode.name || "").trim().toLowerCase();
//           let mainNode = siblings.find((s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm);
//           if (!mainNode) {
//             mainNode = { id: makeId(), name: impNode.name, is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
//             siblings.push(mainNode);
//           }
//           if (!mainNode.children) mainNode.children = [];
//           collectContent(impNode, mainNode.id);
//           expandAdds[curTargetId] = true;
//           expandAdds[mainNode.id] = true;
//           (impNode.children || []).forEach((c) => walkImport(c, mainNode.children));
//         };

//         importFolders.forEach((f) => walkImport(f, targetNode.children));
//       }
//       nextTree = tree;
//     } else {
//       // ── MODE B ── checked + no folder selected
//       const tree = deepClone(allFolders);
//       let seq = 0;
//       const makeId = () => `imp-${Date.now()}-${seq++}`;

//       const hasChecked = (n) =>
//         curCheckedIds.has(n.id) || (n.children || []).some(hasChecked);

//       const processNode = (impNode, siblings) => {
//         if (!hasChecked(impNode)) return false;
//         let mainNode;
//         if (impNode.is_real) {
//           mainNode = siblings.find((s) => s.is_real && s.folderId === impNode.folderId);
//           if (!mainNode) {
//             mainNode = { id: makeId(), name: impNode.name, is_real: true, isPendingNew: false, folderId: impNode.folderId, studyContentId: null, docs: [], videos: [], children: [] };
//             siblings.push(mainNode);
//           }
//         } else {
//           const nm = (impNode.name || "").trim().toLowerCase();
//           mainNode = siblings.find((s) => !s.is_real && (s.name || "").trim().toLowerCase() === nm);
//           if (!mainNode) {
//             mainNode = { id: makeId(), name: impNode.name, is_real: false, isPendingNew: true, folderId: null, studyContentId: null, docs: [], videos: [], children: [] };
//             siblings.push(mainNode);
//           }
//         }
//         if (!mainNode.children) mainNode.children = [];
//         if (curCheckedIds.has(impNode.id)) collectContent(impNode, mainNode.id);
//         let childAdded = false;
//         (impNode.children || []).forEach((c) => { if (processNode(c, mainNode.children)) childAdded = true; });
//         if (childAdded) expandAdds[mainNode.id] = true;
//         return true;
//       };

//       importFolders.forEach((f) => processNode(f, tree));
//       nextTree = tree;
//     }

//     if (nextTree) setAllFolders(nextTree);

//     setFolderPdfs((prev) => {
//       const next = { ...prev };
//       Object.entries(pdfAdds).forEach(([id, entries]) => {
//         next[id] = [...(next[id] || []), ...entries];
//       });
//       return next;
//     });
//     setFolderYoutube((prev) => {
//       const next = { ...prev };
//       Object.entries(ytAdds).forEach(([id, links]) => {
//         const existing = next[id] || { list: [], url: "", error: "" };
//         next[id] = { ...existing, list: [...(existing.list || []), ...links] };
//       });
//       return next;
//     });
//     setExpandedFolders((prev) => ({ ...prev, ...expandAdds }));
//     setIsContentDirty(true);
//     closeImportModal();
//   };

// // ── Folder row renderer ───────────────────────────────────────────────────
//   const renderFolderRow = (folder, depth = 0) => {
//     const isSelected = selectedFolderId === folder.id;
//     const hasChildren = folder.children && folder.children.length > 0;
//     const isExpanded = expandedFolders[folder.id] || false;

//     return (
//       <React.Fragment key={folder.id}>
//         <tr style={{ height: 48 }}>
//           <td style={{ paddingLeft: 36 + depth * 28, paddingTop: 12, paddingBottom: 12, verticalAlign: "middle" }}>
//             <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
//               {hasChildren ? (
//                 <span
//                   style={{ cursor: "pointer", fontWeight: "bold", minWidth: 10, marginRight: 4 }}
//                   onClick={() => toggleExpandFolder(folder.id)}
//                 >
//                   {isExpanded ? "−" : "+"}
//                 </span>
//               ) : (
//                 <span style={{ display: "inline-block", minWidth: 10, marginRight: 4 }} />
//               )}
//               <span
//                 onClick={() => handleSelectFolder(folder.id)}
//                 style={{
//                   cursor: "pointer",
//                   fontSize: 13,
//                   padding: "3px 8px",
//                   borderRadius: 4,
//                   color: isSelected ? "#1a4d8f" : "rgb(66,66,66)",
//                   fontWeight: isSelected ? 700 : 400,
//                   background: isSelected ? "#c9e0ff" : "transparent",
//                   transition: "background 0.15s",
//                 }}
//                 title="Click to select — then type a name above and click ADD SUB-FOLDER"
//               >
//                 {folder.name}
//               </span>
//             </div>
//           </td>
//           <td style={{ verticalAlign: "middle", paddingTop: 12, paddingBottom: 12, paddingRight: 12, paddingLeft: 60 }}>
//             {renderUploadYoutubeRow({
//               id: folder.id,
//               type: "folder",
//               pdfs: folderPdfs,
//               onPdfUpload: handleFolderPdfUpload,
//               onPdfClear: clearFolderPdf,
//               youtube: folderYoutube,
//               onYoutubeChange: async (id, url) => {
//                 setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], url, error: "", urlTitle: "" } }));
//                 const vid = getYoutubeVideoId(url.trim());
//                 if (!vid) return;
//                 if (ytTitleCacheRef.current[url]) {
//                   setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], urlTitle: ytTitleCacheRef.current[url] } }));
//                   return;
//                 }
//                 try {
//                   const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
//                   if (res.ok) {
//                     const data = await res.json();
//                     ytTitleCacheRef.current[url] = data.title;
//                     setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], urlTitle: data.title } }));
//                   }
//                 } catch { /* ignore */ }
//               },
//               onYoutubeAdd: (id) => {
//                 const url = (folderYoutube[id]?.url || "").trim();
//                 if (!url) {
//                   setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], error: "Please enter a YouTube link." } }));
//                   return;
//                 }
//                 if (!getYoutubeVideoId(url)) {
//                   setFolderYoutube((prev) => ({ ...prev, [id]: { ...prev[id], error: "Invalid YouTube URL." } }));
//                   return;
//                 }
//                 setFolderYoutube((prev) => {
//                   const existing = prev[id] || {};
//                   const list = [...(existing.list || []), url];
//                   return { ...prev, [id]: { ...existing, list, url: "", error: "", urlTitle: "" } };
//                 });
//                 setIsContentDirty(true);
//               },
//               onYoutubeClear: (id, idx) => {
//                 setFolderYoutube((prev) => {
//                   const existing = prev[id] || {};
//                   const list = (existing.list || []).filter((_, i) => i !== idx);
//                   return { ...prev, [id]: { ...existing, list } };
//                 });
//                 setIsContentDirty(true);
//               },
//               onYoutubePlay: (link) => {
//                 openYoutubeModal(link);
//               },
//             })}
//           </td>
//           <td style={{ width: 50, textAlign: "center", verticalAlign: "middle" }}>
//             {!folder.is_real && (
//               <button
//                 type="button"
//                 onClick={() => handleDeleteFolder(folder.id)}
//                 style={{ background: "transparent", border: "none", color: "#e53935", cursor: "pointer", fontSize: 15, fontWeight: 700, padding: 0, marginLeft: "460px" }}
//                 title="Delete folder"
//               >✕</button>
//             )}
//           </td>
//         </tr>
//         {isExpanded && hasChildren && folder.children.map((child) => renderFolderRow(child, depth + 1))}
//         {isExpanded && !hasChildren && (
//           <tr>
//             <td
//               colSpan={3}
//               style={{ paddingLeft: 60 + depth * 28, fontSize: 12, color: "#aaa", paddingTop: 4, paddingBottom: 4 }}
//             >
//               No sub-folders
//             </td>
//           </tr>
//         )}
//       </React.Fragment>
//     );
//   };

//   const accessToken = localStorage.getItem("other");

//   useEffect(() => { dispatch(fetchCourseData()); }, [dispatch]);

//   useEffect(() => {
//     const fetchSubjects = async () => {
//       if (value.course_id) {
//         try {
//           const res = await fetch(`${FULL_API_BASE_URL}/get-subjects-by-course/?course_id=${value.course_id}`);
//           const data = await res.json();
//           setSubjects(data);
//         } catch (e) { console.error(e); }
//         setSelectedSubject(null);
//         setSelectedSubjectLabel("");
//       }
//     };
//     fetchSubjects();
//   }, [value.course_id]);

//   useEffect(() => {
//     const fetchBatches = async () => {
//       const res = await fetch(`${FULL_API_BASE_URL}/get-batches/`);
//       const data = await res.json();
//       setBatches(data.data);
//     };
//     fetchBatches();
//   }, []);

//   // ── Load folders from API ─────────────────────────────────────────────────
//   const fetchStudyContent = useCallback(async (signal) => {
//     if (!(value.course_id && selectedSubject && selectedBatch)) {
//       setAllFolders([]);
//       return;
//     }

//     setIsFoldersLoading(true);
//     setFoldersError("");

//     try {
//       const url =
//         `${FULL_API_BASE_URL}/get-study-content/` +
//         `?course_id=${value.course_id}` +
//         `&subject_id=${selectedSubject}` +
//         `&batch_id=${selectedBatch.batch_id}`;

//       const res = await fetch(url, {
//         headers: {
//           accept: "application/json",
//           ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
//         },
//         signal,
//       });

//       if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

//       const json = await res.json();
//       if (json?.status !== 200) throw new Error(json?.message || "Failed to load study content.");

//       const chapters = json?.data?.chapters || [];
//       const customFolders = json?.data?.custom_folders || [];

//       const chapterTree = chapters.map((c) => mapApiFolderToTree(c));
//       const customTree = customFolders.map((c) => mapApiFolderToTree(c, "custom"));
//       const combinedTree = [...chapterTree, ...customTree];

//       setAllFolders(combinedTree);
//       originalFoldersSnapshotRef.current = combinedTree;

//       const pdfsMap = {};
//       const ytMap = {};
//       const pdfBaseline = {};
//       const ytBaseline = {};
//       seedContentState(combinedTree, pdfsMap, ytMap, pdfBaseline, ytBaseline);
//       setFolderPdfs(pdfsMap);
//       setFolderYoutube(ytMap);

//       Object.values(ytMap).forEach(({ list }) => {
//         (list || []).forEach(async (url) => {
//           if (!url || ytTitleCacheRef.current[url]) return;
//           try {
//             const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
//             if (res.ok) {
//               const data = await res.json();
//               ytTitleCacheRef.current[url] = data.title;
//               setFolderYoutube((prev) => ({ ...prev }));
//             }
//           } catch { /* ignore */ }
//         });
//       });

//       folderPdfsBaselineRef.current = pdfBaseline;
//       folderYoutubeBaselineRef.current = ytBaseline;
//     } catch (e) {
//       if (e.name === "AbortError") return;
//       console.error("Failed to fetch study content:", e);
//       setFoldersError("Could not load folders. Please try again.");
//       setAllFolders([]);
//       originalFoldersSnapshotRef.current = [];
//       setFolderPdfs({});
//       setFolderYoutube({});
//       folderPdfsBaselineRef.current = {};
//       folderYoutubeBaselineRef.current = {};
//     } finally {
//       setIsFoldersLoading(false);
//     }

//     setExpandedFolders({});
//     setSelectedFolderId(null);
//     setNewFolderName("");
//     setIsContentDirty(false);
//   }, [value.course_id, selectedSubject, selectedBatch, accessToken]);

//   useEffect(() => {
//     const controller = new AbortController();
//     fetchStudyContent(controller.signal);
//     return () => controller.abort();
//   }, [fetchStudyContent]);

//   // ── Import modal: fetch when batch changes ────────────────────────────────
//   useEffect(() => {
//     if (!importSelectedBatch || !value.course_id || !selectedSubject) {
//       setImportFolders([]);
//       setImportFolderPdfs({});
//       setImportFolderYoutube({});
//       setImportExpandedFolders({});
//       return;
//     }

//     const controller = new AbortController();

//     (async () => {
//       setIsImportFoldersLoading(true);
//       setImportFoldersError("");
//       try {
//         const url =
//           `${FULL_API_BASE_URL}/get-study-content/` +
//           `?course_id=${value.course_id}` +
//           `&subject_id=${selectedSubject}` +
//           `&batch_id=${importSelectedBatch.batch_id}`;

//         const res = await fetch(url, {
//           headers: { accept: "application/json", ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
//           signal: controller.signal,
//         });

//         if (!res.ok) throw new Error(`Status ${res.status}`);
//         const json = await res.json();
//         if (json?.status !== 200) throw new Error(json?.message || "Failed");

//         const chapters = json?.data?.chapters || [];
//         const customFolders = json?.data?.custom_folders || [];
//         const combined = [
//           ...chapters.map((c) => mapApiFolderToTree(c)),
//           ...customFolders.map((c) => mapApiFolderToTree(c, "custom")),
//         ];

//         setImportFolders(combined);

//         const pdfsMap = {};
//         const ytMap = {};
//         const _pb = {}; const _yb = {};
//         seedContentState(combined, pdfsMap, ytMap, _pb, _yb);

//         Object.values(ytMap).forEach(({ list }) => {
//           (list || []).forEach(async (url) => {
//             if (!url || ytTitleCacheRef.current[url]) return;
//             try {
//               const r = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
//               if (r.ok) {
//                 const d = await r.json();
//                 ytTitleCacheRef.current[url] = d.title;
//                 setImportFolderYoutube((p) => ({ ...p }));
//               }
//             } catch { /* ignore */ }
//           });
//         });

//         setImportFolderPdfs(pdfsMap);
//         setImportFolderYoutube(ytMap);
//         setImportExpandedFolders({});
//         setImportCheckedIds(new Set());
//       } catch (e) {
//         if (e.name === "AbortError") return;
//         setImportFoldersError("Could not load folders.");
//         setImportFolders([]);
//       } finally {
//         setIsImportFoldersLoading(false);
//       }
//     })();

//     return () => controller.abort();
//   }, [importSelectedBatch]);

//   // ── YouTube helpers ───────────────────────────────────────────────────────
//   const openYoutubeModal = (url) => {
//     const vid = getYoutubeVideoId(url);
//     if (!vid) return null;
//     setYtVideoId(vid);
//     setIsYoutubeModalOpen(true);
//     return vid;
//   };

//   const clearPlayerTimers = () => {
//     clearInterval(timeUpdaterRef.current);
//     clearTimeout(idleTimerRef.current);
//   };

//   const closeYoutubeModal = useCallback(() => {
//     clearPlayerTimers();
//     if (playerRef.current) {
//       try { playerRef.current.destroy(); } catch { /* ignore */ }
//       playerRef.current = null;
//     }
//     setIsKioskFS(false);
//     setUiHidden(false);
//     setIsPlaying(false);
//     setProgressVal(0);
//     setCurrentTimeFmt("0:00");
//     setTotalTimeFmt("0:00");
//     setIsYoutubeModalOpen(false);
//     setYtVideoId(null);
//   }, []);

//   const showUI = useCallback(() => {
//     setUiHidden(false);
//     clearTimeout(idleTimerRef.current);
//   }, []);

//   const resetIdleTimer = useCallback(() => {
//     showUI();
//     clearTimeout(idleTimerRef.current);
//     if (playerRef.current?.getPlayerState?.() === 1) {
//       idleTimerRef.current = setTimeout(() => setUiHidden(true), 3500);
//     }
//   }, [showUI]);

//   useEffect(() => {
//     if (!isYoutubeModalOpen || !ytVideoId) return;

//     setIsPlaying(false); setProgressVal(0);
//     setCurrentTimeFmt("0:00"); setTotalTimeFmt("0:00");
//     setUiHidden(false); setSpeed(1);

//     onYTReady(() => {
//       if (!ytDivRef.current) return;
//       if (playerRef.current) { try { playerRef.current.destroy(); } catch { /* ignore */ } }

//       playerRef.current = new window.YT.Player(ytDivRef.current, {
//         videoId: ytVideoId,
//         playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, playsinline: 1 },
//         events: {
//           onReady: (e) => {
//             const dur = e.target.getDuration();
//             setDurationVal(dur);
//             setTotalTimeFmt(formatTime(dur));
//             resetIdleTimer();
//           },
//           onStateChange: (e) => {
//             const YT = window.YT.PlayerState;
//             if (e.data === YT.PLAYING) {
//               setIsPlaying(true);
//               resetIdleTimer();
//               clearInterval(timeUpdaterRef.current);
//               timeUpdaterRef.current = setInterval(() => {
//                 const ct = playerRef.current?.getCurrentTime?.() || 0;
//                 const dur = playerRef.current?.getDuration?.() || 0;
//                 setProgressVal(ct);
//                 setCurrentTimeFmt(formatTime(ct));
//                 if (dur) setTotalTimeFmt(formatTime(dur));
//               }, 500);
//             } else {
//               setIsPlaying(false);
//               showUI();
//               clearInterval(timeUpdaterRef.current);
//               clearTimeout(idleTimerRef.current);
//             }
//             if (e.data === YT.ENDED) {
//               playerRef.current?.seekTo(0);
//               playerRef.current?.pauseVideo();
//             }
//           },
//         },
//       });
//     });

//     return () => clearPlayerTimers();
//   }, [isYoutubeModalOpen, ytVideoId]);

//   useEffect(() => () => {
//     clearPlayerTimers();
//     playerRef.current?.destroy?.();
//   }, []);

//   const togglePlay = () => {
//     const p = playerRef.current;
//     if (!p) return;
//     p.getPlayerState() === 1 ? p.pauseVideo() : p.playVideo();
//     resetIdleTimer();
//   };

//   const handleSeek = (e) => {
//     const val = parseFloat(e.target.value);
//     playerRef.current?.seekTo(val, true);
//     setProgressVal(val);
//     resetIdleTimer();
//   };

//   const handleSpeed = (e) => {
//     const val = parseFloat(e.target.value);
//     playerRef.current?.setPlaybackRate(val);
//     setSpeed(val);
//     resetIdleTimer();
//   };

//   const handleFullscreen = () => {
//     const el = containerRef.current;
//     if (!el) return;
//     const isFS = document.fullscreenElement || document.webkitFullscreenElement || isKioskFS;
//     if (!isFS) {
//       if (el.requestFullscreen) {
//         el.requestFullscreen()
//           .then(() => window.screen.orientation?.lock?.("landscape").catch(() => {}))
//           .catch(() => { setIsKioskFS(true); window.screen.orientation?.lock?.("landscape").catch(() => {}); });
//       } else if (el.webkitRequestFullscreen) {
//         el.webkitRequestFullscreen();
//         setIsKioskFS(true);
//         window.screen.orientation?.lock?.("landscape").catch(() => {});
//       } else {
//         setIsKioskFS(true);
//         window.screen.orientation?.lock?.("landscape").catch(() => {});
//       }
//     } else {
//       setIsKioskFS(false);
//       window.screen.orientation?.unlock?.();
//       document.exitFullscreen?.().catch(() => {});
//       document.webkitExitFullscreen?.();
//     }
//   };

//   useEffect(() => {
//     const onChange = () => {
//       if (!document.fullscreenElement && !document.webkitFullscreenElement) {
//         setIsKioskFS(false);
//         window.screen.orientation?.unlock?.();
//       }
//     };
//     document.addEventListener("fullscreenchange", onChange);
//     document.addEventListener("webkitfullscreenchange", onChange);
//     return () => {
//       document.removeEventListener("fullscreenchange", onChange);
//       document.removeEventListener("webkitfullscreenchange", onChange);
//     };
//   }, []);

//   // ── Save ──────────────────────────────────────────────────────────────────
//   const handleSave = async () => {
//     if (!isContentDirty) {
//       setIsMessageBoxVisible(true);
//       setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>Nothing to save.</span></div></div>);
//       setActiveImg(false);
//       return;
//     }
//     if (!(value.course_id && selectedSubject && selectedBatch)) {
//       setIsMessageBoxVisible(true);
//       setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>Select Course, Subject and Batch first.</span></div></div>);
//       setActiveImg(false);
//       return;
//     }

//     setIsSaving(true);
//     const uploadedBy = userName;

//     try {
//       const currentIds = new Set();
//       collectStudyContentIds(allFolders, currentIds);
//       const deletedIds = [];
//       collectDeletedRoots(originalFoldersSnapshotRef.current, currentIds, deletedIds);

//       for (const id of deletedIds) {
//         await saveStudyContentBatch({
//           uploadedBy,
//           item: { op: "delete", study_content_id: id },
//           accessToken,
//         });
//       }

//       const walk = async (node, ancestorPath) => {
//         let ownId = node.studyContentId;
//         const ownIsReal = node.is_real;

//         const pdfList = folderPdfs[node.id] || [];
//         const newPdfFiles = pdfList.filter((p) => p.file).map((p) => p.file);
//         const currentPdfKeys = pdfList.filter((p) => p.pdfFileName).map((p) => p.pdfFileName);
//         const baselinePdfKeys = folderPdfsBaselineRef.current[node.id] || [];
//         const removedPdfKeys = diffList(currentPdfKeys, baselinePdfKeys).removed;

//         const ytData = folderYoutube[node.id] || {};
//         const currentYtLinks = ytData.list || [];
//         const baselineYtLinks = folderYoutubeBaselineRef.current[node.id] || [];
//         const { added: newYtLinks, removed: removedYtLinks } = diffList(currentYtLinks, baselineYtLinks);

//         const needsChange = newPdfFiles.length || removedPdfKeys.length || newYtLinks.length || removedYtLinks.length;

//         if (node.isPendingNew) {
//           const { chapter_id, topic_id } = pinnedFieldsFromPath(ancestorPath);
//           const item = {
//             op: "create",
//             batch_id: selectedBatch.batch_id,
//             subject_id: selectedSubject,
//             chapter_id,
//             topic_id,
//             folder_path: [
//               ...ancestorPath.map((p) => ({ id: p.id, name: p.name })),
//               { id: null, name: node.name },
//             ],
//           };
//           if (newYtLinks.length) item.add_yt_video_urls = newYtLinks;

//           const result = await saveStudyContentBatch({
//             uploadedBy,
//             item,
//             pdfFiles: newPdfFiles,
//             accessToken,
//           });
//           ownId = result.study_content_id;
//         } else if (!ownId && needsChange) {
//           const selfEntry = { id: node.folderId, name: node.name, is_real: node.is_real };
//           const fullPath = [...ancestorPath, selfEntry];
//           const { chapter_id, topic_id } = pinnedFieldsFromPath(fullPath);
//           const item = {
//             op: "create",
//             batch_id: selectedBatch.batch_id,
//             subject_id: selectedSubject,
//             chapter_id,
//             topic_id,
//             folder_path: fullPath.map((p) => ({ id: p.id, name: p.name })),
//           };
//           if (newYtLinks.length) item.add_yt_video_urls = newYtLinks;

//           await saveStudyContentBatch({ uploadedBy, item, pdfFiles: newPdfFiles, accessToken });
//           ownId = node.folderId;
//         } else if (ownId && needsChange) {
//           const item = { op: "update", study_content_id: ownId };
//           if (newYtLinks.length) item.add_yt_video_urls = newYtLinks;
//           if (removedYtLinks.length) item.remove_yt_video_urls = removedYtLinks;
//           if (removedPdfKeys.length) item.remove_pdf_keys = removedPdfKeys;

//           await saveStudyContentBatch({ uploadedBy, item, pdfFiles: newPdfFiles, accessToken });
//         }

//         const pathId = node.isPendingNew ? ownId : (ownIsReal ? node.folderId : ownId);
//         const nextAncestorPath = [...ancestorPath, { id: pathId, name: node.name, is_real: ownIsReal }];
//         for (const child of (node.children || [])) {
//           await walk(child, nextAncestorPath);
//         }
//       };

//       for (const root of allFolders) {
//         await walk(root, []);
//       }

//       setIsMessageBoxVisible(true);
//       setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>All changes saved successfully.</span></div></div>);
//       setActiveImg(false);

//       await fetchStudyContent();
//     } catch (e) {
//       console.error(e);
//       setIsMessageBoxVisible(true);
//       setMessageBox(<div className="Container-messageBOX"><div className="messagebox-insider1"><div className="loader-tc"></div><span style={{ marginLeft: 10, marginTop: 3 }}>Could not save everything: {e.message}</span></div></div>);
//       setActiveImg(false);
//       await fetchStudyContent();
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   // ── renderUploadYoutubeRow ────────────────────────────────────────────────
//   const renderUploadYoutubeRow = ({ id, type, pdfs, onPdfUpload, onPdfClear, youtube, onYoutubeChange, onYoutubeAdd, onYoutubeClear, onYoutubePlay }) => {
//     const pdfList = pdfs[id] || [];
//     const ytData = youtube[id] || {};
//     const ytList = ytData.list || [];
//     const inputId = `pdf-input-${type}-${id}`;

//     const renderPdfName = (pdf, index) => (
//       <div style={{ position: "relative", width: 280, flexShrink: 0 }}>
//         <span
//           style={{
//             display: "block", fontSize: 11, color: "#333", background: "#eef4ff",
//             border: "1px solid #c9e0ff", borderRadius: 4, padding: "3px 48px 3px 8px",
//             overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
//             height: 22, boxSizing: "border-box", lineHeight: "15px",
//           }}
//         >
//           {pdf.fileName}
//         </span>
//         <div
//           style={{
//             position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)",
//             display: "flex", alignItems: "center", gap: 2, background: "#eef4ff", paddingLeft: 2,
//           }}
//         >
//           <button
//             type="button"
//             onClick={() => { setModalPdfUrl(pdf.previewUrl); setModalPdfName(pdf.fileName); setIsPdfModalOpen(true); }}
//             style={{ width: 18, height: 18, padding: 0, background: "transparent", color: "#2e7d32", border: "none", cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
//             title="View PDF"
//           >👁</button>
//           <button
//             type="button"
//             onClick={() => onPdfClear(id, index)}
//             style={{ width: 18, height: 18, padding: 0, background: "transparent", color: "#e53935", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
//             title="Remove PDF"
//           >✕</button>
//         </div>
//       </div>
//     );

//     const renderYoutubeChip = (link, index) => {
//       const chipTitle = ytTitleCacheRef.current[link] || link;
//       return (
//         <div style={{ position: "relative", width: 280, flexShrink: 0 }}>
//           <span
//             style={{
//               display: "block", fontSize: 11, color: "#333",
//               border: "1px solid rgb(204, 204, 204)", borderRadius: 4, padding: "3px 48px 3px 8px",
//               overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
//               height: 22, boxSizing: "border-box", lineHeight: "15px",
//             }}
//             title={chipTitle}
//           >
//             {chipTitle}
//           </span>
//           <div
//             style={{
//               position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)",
//               display: "flex", alignItems: "center", gap: 2, background: "#fff0f0", paddingLeft: 2,
//             }}
//           >
//             <button
//               type="button"
//               onClick={() => onYoutubePlay(link)}
//               style={{ width: 18, height: 18, padding: 0, color: "#e50914", border: "none", cursor: "pointer", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
//               title="Play YouTube"
//             >▶</button>
//             <button
//               type="button"
//               onClick={() => onYoutubeClear(id, index)}
//               style={{ width: 18, height: 18, padding: 0, color: "#e53935", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
//               title="Remove link"
//             >✕</button>
//           </div>
//         </div>
//       );
//     };

//     return (
//       <div style={{ display: "flex", flexDirection: "column" }}>
//         <div style={{ display: "flex", alignItems: "flex-start", gap: 6, flexWrap: "nowrap", marginLeft: "365px" }}>
//           <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3 }}>
//             {pdfList.map((pdf, idx) => (
//               <div key={idx}>{renderPdfName(pdf, idx)}</div>
//             ))}
//           </div>
//           <label htmlFor={inputId} className="browse-button-study-content" title="Upload PDF">
//             <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//               <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
//               <polyline points="14 2 14 8 20 8" />
//               <line x1="12" y1="18" x2="12" y2="12" />
//               <polyline points="9 15 12 12 15 15" />
//             </svg>
//             <span style={{ fontSize: 7, fontWeight: 700, letterSpacing: 0.3, lineHeight: 1 }}>PDF</span>
//           </label>
//           <input
//             id={inputId} type="file" accept=".pdf" multiple style={{ display: "none" }}
//             onChange={(e) => { onPdfUpload(id, e.target.files); e.target.value = ""; }}
//           />
//           <div style={{ width: 96, flexShrink: 0 }} />
//           <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 3 }}>
//             {ytList.map((link, idx) => (
//               <div key={idx}>{renderYoutubeChip(link, idx)}</div>
//             ))}
//           </div>
//           <div style={{ position: "relative", flexShrink: 0, marginRight: "180" }}>
//             <input
//               type="text"
//               value={ytData.url || ""}
//               onChange={(e) => onYoutubeChange(id, e.target.value)}
//               onKeyDown={(e) => { if (e.key === "Enter") onYoutubeAdd(id); }}
//               placeholder="Enter YouTube video URL"
//               style={{ width: 160, flexShrink: 0, padding: "4px 10px", border: `1px solid ${ytData.error ? "#e53935" : "#ccc"}`, borderRadius: 5, fontSize: 12, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
//             />
//             {ytData.urlTitle && (
//               <span style={{
//                 position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
//                 fontSize: 10, color: "#4caf50", pointerEvents: "none",
//               }}>✓</span>
//             )}
//           </div>
//         </div>
//         {ytData.error && (
//           <span style={{ fontSize: 11, color: "#e53935", display: "block", paddingLeft: 490, whiteSpace: "nowrap" }}>
//             ⚠ {ytData.error}
//           </span>
//         )}
//       </div>
//     );
//   };

//   const ctrlBtn = {
//     background: "#e50914", color: "#fff", border: "none",
//     minWidth: 44, height: 38, padding: "0 12px",
//     borderRadius: 6, cursor: "pointer", fontWeight: "bold", fontSize: 14,
//   };

//   // ── Import folder row renderer (read-only with checkbox) ──────────────────
//   const renderImportFolderRow = (folder, depth = 0) => {
//     const isExpanded = importExpandedFolders[folder.id] || false;
//     const pdfList = importFolderPdfs[folder.id] || [];
//     const ytList = (importFolderYoutube[folder.id] || {}).list || [];
//     const hasContent = pdfList.length > 0 || ytList.length > 0;
//     const childrenWithContent = (folder.children || []).filter((c) =>
//       hasAnyContent(c, importFolderPdfs, importFolderYoutube)
//     );
//     const hasChildren = childrenWithContent.length > 0;

//     if (!hasContent && !hasChildren) return null;

//     return (
//       <React.Fragment key={folder.id}>
//         <tr style={{ height: 40 }}>
//           <td style={{ paddingLeft: 24 + depth * 32, verticalAlign: "baseline", width: 310 }}>
//             <div style={{ display: "flex", alignItems: "center" }}>
//               <input
//                 type="checkbox"
//                 className="delete-question-checkbox-dqc"
//                 checked={importCheckedIds.has(folder.id)}
//                 onChange={(e) => handleImportCheckbox(folder, e.target.checked)}
//                 style={{ flexShrink: 0 }}
//               />
//               <span
//                 style={{ cursor: hasChildren ? "pointer" : "default", fontWeight: "bold", minWidth: 15, flexShrink: 0 }}
//                 onClick={() => hasChildren && setImportExpandedFolders((p) => ({ ...p, [folder.id]: !p[folder.id] }))}
//               >
//                 {hasChildren ? (isExpanded ? "−" : "+") : ""}
//               </span>
//               <span style={{ fontSize: 13, color: "rgb(66,66,66)" }}>
//                 {folder.name}
//               </span>
//             </div>
//           </td>

//           <td style={{ verticalAlign: "baseline", paddingTop: 10, paddingBottom: 10, paddingLeft: 189, width: 200 }}>
//             <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
//               {pdfList.map((pdf, i) => (
//                 <span
//                   key={i}
//                   style={{ display: "block", fontSize: 11, color: "#333", border: "1px solid #ccc", borderRadius: 4, padding: "3px 8px", width: 220, boxSizing: "border-box", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
//                   title={pdf.fileName}
//                 >
//                   {pdf.fileName}
//                 </span>
//               ))}
//             </div>
//           </td>

//           <td style={{ verticalAlign: "middle", paddingTop: 10, paddingBottom: 10, paddingLeft: 16 }}>
//             <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
//               {ytList.map((link, i) => {
//                 const title = ytTitleCacheRef.current[link] || link;
//                 return (
//                   <span
//                     key={i}
//                     style={{ display: "block", fontSize: 11, color: "#333", border: "1px solid #ccc", borderRadius: 4, padding: "3px 8px", width: 260, boxSizing: "border-box", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
//                     title={title}
//                   >
//                     {title}
//                   </span>
//                 );
//               })}
//             </div>
//           </td>
//         </tr>

//         {isExpanded && childrenWithContent.map((c) => renderImportFolderRow(c, depth + 1))}
//       </React.Fragment>
//     );
//   };

//   // Text shown under the modal title: where the import will go
//   const importTargetPath = importTargetFolderId
//     ? getFolderPath(allFolders, importTargetFolderId)
//     : null;

//   return (
//     <div className="create-role-container">
//       <div className="create-role-container-ques-paper-del">
//         <span className="create-role-heading">Study Resources</span>
//         <div className="cr-header-btn-container">
//           <button className="cr-cancel-btn">CANCEL</button>
//           <button className="cr-cancel-btn" onClick={openImportModal}>IMPORT</button>
//           <button className="cr-create-btn" onClick={handleSave} disabled={isSaving}>SAVE</button>
//         </div>
//       </div>

//       {/* SECTION 1: BATCH DETAILS */}
//       <div className="ques-paper-del">
//         <div className="personal-header">
//           <div className="header"><label>BATCH DETAILS</label></div>
//           <div className="accordion-btn" onClick={() => setIsOpenSubject((p) => !p)}>
//             {isOpenSubject ? <div className="minus"></div> : <div className="plus">+</div>}
//           </div>
//         </div>
//         {isOpenSubject && (
//           <div className="accordion-content">
//             <div className="select-assign-test-container-ques-pap-del">
//               <div className="grid-container-ques-paper-del">
//                 <div className="item1">
//                   <DropDown
//                     label="Course"
//                     setSelected={handleCourseChange}
//                     options={coursesdata ? coursesdata.map((c) => ({ label: c.course_description, id: c.course_id })) : []}
//                     isRequired={true}
//                   />
//                 </div>
//                 <div className="item2">
//                   <DropDown
//                     label="Subject"
//                     setSelected={handleSubjectChange}
//                     selectedLabel={selectedSubjectLabel}
//                     setSelectedLabel={setSelectedSubjectLabel}
//                     options={subjects.map((s) => ({ label: s.subject_name, id: s.subject_id }))}
//                     isRequired={true}
//                   />
//                 </div>
//                 <div className="item3">
//                   <DropDown
//                     label="Batch Name"
//                     style={{ width: "100%" }}
//                     selected={selectedBatch ? selectedBatch.batch_id : null}
//                     setSelected={handleBatchSelection}
//                     options={batches.map((b) => ({ id: b.batch_id, label: b.batch_name }))}
//                     isRequired={true}
//                     Height="auto"
//                     minheight="200px"
//                     OverFlow="auto"
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* ── PDF MODAL ── */}
//       {isPdfModalOpen && (
//         <div
//           onClick={() => { setIsPdfModalOpen(false); setModalPdfUrl(null); }}
//           style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }}
//         >
//           <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "min(1200px, 95vw)" }}>
//             <button
//               type="button"
//               onClick={() => { setIsPdfModalOpen(false); setModalPdfUrl(null); }}
//               style={{ position: "absolute", top: -10, right: -58, background: "transparent", border: "none", color: "#fff", width: 36, height: 36, fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
//             >✕</button>
//             <div style={{ borderRadius: 10, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}>
//               <iframe
//                 src={`${modalPdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
//                 title="PDF Preview"
//                 width="100%"
//                 height="760px"
//                 style={{ border: "none", display: "block" }}
//               />
//             </div>
//           </div>
//         </div>
//       )}

//       {/* ── YOUTUBE PLAYER MODAL ── */}
//       {isYoutubeModalOpen && (
//         <div
//           onClick={closeYoutubeModal}
//           style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }}
//         >
//           <div
//             ref={containerRef}
//             onClick={(e) => e.stopPropagation()}
//             style={{
//               position: isKioskFS ? "fixed" : "relative",
//               ...(isKioskFS
//                 ? { inset: 0, width: "100vw", height: "100dvh", maxWidth: "none", borderRadius: 0, zIndex: 999999 }
//                 : { width: "min(860px, 95vw)", borderRadius: 10 }),
//               background: "#000", overflow: "hidden",
//               boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
//               display: "flex", flexDirection: "column",
//             }}
//           >
//             <button
//               type="button"
//               onClick={closeYoutubeModal}
//               onMouseMove={resetIdleTimer}
//               style={{
//                 position: "absolute", top: 15, left: 15, zIndex: 40,
//                 width: 44, height: 44, background: "rgba(0,0,0,0.65)",
//                 border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50%",
//                 display: "flex", alignItems: "center", justifyContent: "center",
//                 cursor: "pointer",
//                 opacity: uiHidden ? 0 : 1,
//                 visibility: uiHidden ? "hidden" : "visible",
//                 transition: "opacity 0.3s, visibility 0.3s",
//                 pointerEvents: uiHidden ? "none" : "auto",
//               }}
//               title="Close"
//             >
//               <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
//                 <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
//               </svg>
//             </button>

//             <div style={{
//               position: "absolute", top: 5, right: 5, zIndex: 30,
//               pointerEvents: "none",
//               opacity: uiHidden ? 0.2 : 0.85,
//               transition: "opacity 0.3s",
//             }}>
//               <span style={{ color: "#ff0000", fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>▶ DSE</span>
//             </div>

//             <div
//               style={{ position: "relative", paddingBottom: isKioskFS ? 0 : "56.25%", height: isKioskFS ? 0 : 0, flexGrow: isKioskFS ? 1 : 0, overflow: "hidden", background: "#000" }}
//               onMouseMove={resetIdleTimer}
//             >
//               <div
//                 ref={ytDivRef}
//                 style={{ position: "absolute", top: -80, left: 0, width: "100%", height: "calc(100% + 220px)", pointerEvents: "none" }}
//               />
//               <div
//                 style={{ position: "absolute", inset: 0, zIndex: 20, cursor: "pointer" }}
//                 onClick={() => {
//                   if (uiHidden) { resetIdleTimer(); }
//                   else { togglePlay(); resetIdleTimer(); }
//                 }}
//               />
//             </div>

//             <div
//               onMouseMove={resetIdleTimer}
//               style={{
//                 ...(isKioskFS
//                   ? { position: "absolute", bottom: 0, left: 0, width: "100%", background: "rgba(24,24,24,0.85)" }
//                   : { background: "#181818" }),
//                 display: "flex", flexWrap: "wrap", alignItems: "center",
//                 padding: "12px 16px", gap: 12,
//                 opacity: uiHidden ? 0 : 1,
//                 visibility: uiHidden ? "hidden" : "visible",
//                 transition: "opacity 0.3s, visibility 0.3s",
//                 pointerEvents: uiHidden ? "none" : "auto",
//                 zIndex: 40,
//               }}
//             >
//               <input
//                 type="range" min={0} max={durationVal || 100} step={0.1} value={progressVal}
//                 onChange={handleSeek}
//                 style={{ flexBasis: "100%", order: -1, height: 6, accentColor: "#e50914", cursor: "pointer", marginBottom: 4 }}
//               />
//               <button type="button" style={ctrlBtn} onClick={() => { togglePlay(); resetIdleTimer(); }}>
//                 {isPlaying ? "Pause" : "Play"}
//               </button>
//               <span style={{ color: "#ccc", fontSize: 13, minWidth: 75, textAlign: "center" }}>
//                 {currentTimeFmt} / {totalTimeFmt}
//               </span>
//               <select
//                 value={speed} onChange={handleSpeed}
//                 style={{ background: "#2a2a2a", color: "#fff", border: "1px solid #444", height: 38, padding: "0 8px", borderRadius: 6, fontSize: 13 }}
//               >
//                 <option value={1}>1x</option>
//                 <option value={1.25}>1.25x</option>
//                 <option value={1.5}>1.5x</option>
//                 <option value={2}>2x</option>
//               </select>
//               <button type="button" style={{ ...ctrlBtn, marginLeft: "auto" }} onClick={handleFullscreen}>
//                 {isKioskFS || document.fullscreenElement ? "Exit" : "Full"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* CONTENT FOLDER SECTION */}
//       <div className="ques-paper-del">
//         <div className="personal-header">
//           <div className="header"><label>CONTENT FOLDER</label></div>
//           <div className="accordion-btn" onClick={() => setIsOpenCustomFolder((p) => !p)}>
//             {isOpenCustomFolder ? <div className="minus"></div> : <div className="plus">+</div>}
//           </div>
//         </div>
//         {isOpenCustomFolder && (
//           <div className="accordion-content">
//             <div style={{ padding: "10px 16px 14px" }}>
//               <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
//                 <input
//                   type="text"
//                   value={newFolderName}
//                   onChange={(e) => setNewFolderName(e.target.value)}
//                   onKeyDown={(e) => { if (e.key === "Enter") handleAddFolder(); }}
//                   placeholder={selectedFolderId ? "Enter sub-folder name..." : "Enter folder name..."}
//                   style={{ flex: 1, maxWidth: 320, padding: "6px 12px", border: "1px solid #ccc", borderRadius: 5, fontSize: 13, outline: "none", color: "rgb(66,66,66)", transition: "border 0.2s" }}
//                 />
//                 <button
//                   type="button"
//                   onClick={handleAddFolder}
//                   style={{ padding: "6px 18px", background: "#2F2E40", color: "#fff", border: "none", borderRadius: 5, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
//                 >
//                   ADD FOLDER
//                 </button>
//               </div>
//               {selectedFolderId && (() => {
//                 const path = getFolderPath(allFolders, selectedFolderId);
//                 return path ? (
//                   <div style={{ marginTop: 6, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
//                     {path.map((segment, i) => (
//                       <React.Fragment key={i}>
//                         <span style={{ fontSize: 12, color: "#555" }}>{segment}</span>
//                         <span style={{ fontSize: 12, color: "#aaa", margin: "0 2px" }}>\</span>
//                       </React.Fragment>
//                     ))}
//                     {newFolderName.trim() && (
//                       <>
//                         <span style={{ fontSize: 12, color: "#555" }}>{newFolderName}</span>
//                         <span style={{ fontSize: 12, color: "#aaa", margin: "0 2px" }}>\</span>
//                       </>
//                     )}
//                   </div>
//                 ) : null;
//               })()}
//             </div>

//             {isFoldersLoading && (
//               <div style={{ padding: "10px 16px", fontSize: 13, color: "#888" }}>Loading folders...</div>
//             )}
//             {!isFoldersLoading && foldersError && (
//               <div style={{ padding: "10px 16px", fontSize: 13, color: "#e53935" }}>⚠ {foldersError}</div>
//             )}
//             {!isFoldersLoading && !foldersError && allFolders.length > 0 && (
//               <table className="table-tc-create-test" style={{ opacity: 0.8, width: "100%", tableLayout: "fixed" }}>
//                 <thead>
//                   <tr>
//                     <td style={{ paddingLeft: 24, borderBottom: "1px solid #e0e0e0" }}>Folder Name</td>
//                     <td style={{ paddingLeft: 310, borderBottom: "1px solid #e0e0e0" }}>PDF</td>
//                     <td style={{ paddingLeft: 232, borderBottom: "1px solid #e0e0e0" }}>YouTube</td>
//                     <td style={{ width: 50, borderBottom: "1px solid #e0e0e0" }}></td>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {allFolders.map((folder) => renderFolderRow(folder, 0))}
//                 </tbody>
//               </table>
//             )}
//             {!isFoldersLoading && !foldersError && allFolders.length === 0 && (
//               <div style={{ padding: "10px 16px", fontSize: 13, color: "#bbb" }}>
//                 {value.course_id && selectedSubject && selectedBatch
//                   ? "No folders for this selection. Add one above."
//                   : "Select Course, Subject and Batch to load folders, or add one manually."}
//               </div>
//             )}
//           </div>
//         )}
//       </div>

//       {/* ── IMPORT MODAL ── */}
//       {isImportModalOpen && (
//         <div
//           onClick={closeImportModal}
//           style={{ position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999 }}
//         >
//           <div
//             onClick={(e) => e.stopPropagation()}
//             style={{ position: "relative", background: "#fff", borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.5)", padding: "28px 32px 24px", width: "min(1200px, 95vw)", maxHeight: "83vh", display: "flex", flexDirection: "column" }}
//           >
//             <button
//               type="button"
//               onClick={closeImportModal}
//               style={{ position: "absolute", top: -10, right: -58, background: "transparent", border: "none", color: "#fff", width: 36, height: 36, fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
//             >✕</button>

//             {/* Modal header with IMPORT button */}
//             {/* <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
//               <span style={{ fontWeight: 700, fontSize: 15, color: "#2F2E40", letterSpacing: 0.3 }}>
//                 IMPORT FROM BATCH
//               </span>
//               <button
//                 type="button"
//                 disabled={importCheckedIds.size === 0}
//                 onClick={handleImportConfirm}
//                 style={{
//                   padding: "6px 20px",
//                   background: importCheckedIds.size === 0 ? "#ccc" : "#2F2E40",
//                   color: "#fff",
//                   border: "none",
//                   borderRadius: 5,
//                   fontSize: 13,
//                   fontWeight: 600,
//                   cursor: importCheckedIds.size === 0 ? "not-allowed" : "pointer",
//                   transition: "background 0.2s",
//                 }}
//               >
//                 IMPORT {importCheckedIds.size > 0 ? `(${importCheckedIds.size})` : ""}
//               </button>
//             </div> */}
//           {/* Modal header with IMPORT button */}
//           <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
//             <span style={{ fontWeight: 700, fontSize: 15, color: "#2F2E40", letterSpacing: 0.3 }}>
//               IMPORT FROM BATCH
//             </span>
//             <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
//               <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#2F2E40", cursor: "pointer", userSelect: "none" }}>
//                 <input
//                   type="checkbox"
//                   className="delete-question-checkbox-dqc"
//                   checked={importContentWithFolder}
//                   onChange={(e) => setImportContentWithFolder(e.target.checked)}
//                   style={{ flexShrink: 0 }}
//                 />
//                 Content with folder
//               </label>
//               <button
//                 type="button"
//                 disabled={importCheckedIds.size === 0}
//                 onClick={handleImportConfirm}
//                 style={{
//                   padding: "6px 20px",
//                   background: importCheckedIds.size === 0 ? "#ccc" : "#2F2E40",
//                   color: "#fff",
//                   border: "none",
//                   borderRadius: 5,
//                   fontSize: 13,
//                   fontWeight: 600,
//                   cursor: importCheckedIds.size === 0 ? "not-allowed" : "pointer",
//                   transition: "background 0.2s",
//                 }}
//               >
//                 IMPORT {importCheckedIds.size > 0 ? `(${importCheckedIds.size})` : ""}
//               </button>
//             </div>
//           </div>
//             {/* Where the import will go */}
//             <div style={{ fontSize: 12, color: "#777", marginBottom: 14 }}>
//               {importTargetPath
//                 ? <>Content of the checked folders will be added into:<b>{importTargetPath.join("\\")}</b></>
//                 : "Checked folders will be added to the Content Folder list as folders."}
//             </div>

//             <div style={{ marginBottom: 16, maxWidth: 320 }}>
//               <DropDown
//                 label="Batch Name"
//                 selected={importSelectedBatch ? importSelectedBatch.batch_id : null}
//                 setSelected={(id) => setImportSelectedBatch(batches.find((b) => b.batch_id === id))}
//                 options={batches
//                   .filter((b) => !selectedBatch || b.batch_id !== selectedBatch.batch_id)
//                   .map((b) => ({ id: b.batch_id, label: b.batch_name }))}
//                 isRequired={true}
//                 Height="auto"
//                 minheight="200px"
//                 OverFlow="auto"
//               />
//             </div>

//             {isImportFoldersLoading && (
//               <div style={{ fontSize: 13, color: "#888", padding: "10px 0" }}>Loading folders...</div>
//             )}
//             {!isImportFoldersLoading && importFoldersError && (
//               <div style={{ fontSize: 13, color: "#e53935", padding: "10px 0" }}>⚠ {importFoldersError}</div>
//             )}
//             {!isImportFoldersLoading && !importFoldersError && importFolders.length > 0 && (
//               <div style={{ overflowY: "auto", flex: 1 }}>
//                 <table style={{ width: "100%", borderCollapse: "collapse" }}>
//                   <thead>
//                     <tr>
//                       <td style={{ fontSize: 12, fontWeight: 600, color: "#555", paddingBottom: 8, paddingLeft: 12, borderBottom: "1px solid #e0e0e0", width: 280 }}>Chapter/Topic</td>
//                       <td style={{ fontSize: 12, fontWeight: 600, color: "#555", paddingBottom: 8, paddingLeft: 285, borderBottom: "1px solid #e0e0e0", width: 260 }}>PDF</td>
//                       <td style={{ fontSize: 12, fontWeight: 600, color: "#555", paddingBottom: 8, paddingLeft: 191, borderBottom: "1px solid #e0e0e0" }}>YouTube</td>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {importFolders
//                       .filter((f) => hasAnyContent(f, importFolderPdfs, importFolderYoutube))
//                       .map((f) => renderImportFolderRow(f, 0))}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//             {!isImportFoldersLoading && !importFoldersError && importFolders.length === 0 && importSelectedBatch && (
//               <div style={{ fontSize: 13, color: "#bbb", padding: "10px 0" }}>No content found for this batch.</div>
//             )}
//             {!importSelectedBatch && (
//               <div style={{ fontSize: 13, color: "#bbb", padding: "10px 0" }}>Select a batch to preview its content.</div>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default PdfViewer;